import { CommonModule, TitleCasePipe } from '@angular/common';
import { Title } from '@angular/platform-browser';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDateRangePicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { StatusMainPageInternalSubscriptionDialogComponent } from './subscription-dialog/internal-subscription-dialog/internal-subscription-dialog.component';
import { StatusMainPagePublicSubscriptionDialogComponent } from './subscription-dialog/public-subscription-dialog/public-subscription-dialog.component';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { StatusPageCapabilitiesService } from '../services/status-page-capabilities.service';
import { AuthService } from 'src/app/_theme/core/users/Auth/_services/auth.service';
import { AuthHTTPService } from 'src/app/_theme/core/users/Auth/_services/auth-http/auth-http.service';
import { UserProfileServices } from 'src/app/_theme/core/users/user_profile/_services/user-profile.services';
import { AbilitiesService } from 'src/app/_theme/core/abilities/abilities.service';
import {
  STATUS_MAIN_DEFAULT_DESCRIPTION,
  STATUS_MAIN_DEFAULT_DOMAIN,
  STATUS_MAIN_DEFAULT_OVERALL_STATUS,
  STATUS_MAIN_DEFAULT_PRIMARY_COLOR,
  STATUS_MAIN_DEFAULT_TITLE,
  STATUS_MAIN_SSO_TOKEN_STORAGE_KEY
} from './core/status-main-page.constants';
import { STATUS_MAIN_ERROR_MESSAGES } from './core/status-main-page.error-messages';
import {
  mapIncidentStatusLabel,
  mapMaintenanceStatusLabel,
  mapStatusClass,
  normalizeGroupedServiceItems,
  normalizeGroupedServices,
  normalizeIncidents,
  normalizeMaintenances
} from './core/status-main-page.mappers';
import {
  AliasResolutionResult,
  GroupedServiceGroup,
  IncidentHistoryDetailState,
  PublicIncidentItem,
  PublicIncidentUpdateItem,
  PublicIncidentPostmortem,
  PublicMaintenanceItem,
  ServiceItem,
  StatusMainPageMode
} from './core/status-main-page.models';
import {
  extractOrgSlugFromAliasResponse,
  getAliasCandidatesFromHost,
  getStatusRoutePartsFromUrl,
  isAppHost,
  resolveDomainNameFromHost
} from './core/status-main-page.url-resolver';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {
  normalizeStatusPageRichText,
  normalizeStatusPageRichTextWithFallback
} from '../shared/models/status-page-rich-text-normalizer.utils';
import { StatusPageRichTextPipe } from '../shared/pipes/status-page-rich-text.pipe';

interface StatusTimeDisplay {
  dateLabel: string;
  timeLabel: string;
  dateTimeLabel: string;
  timeWithZoneLabel: string;
  timeZoneLabel: string;
}
type DateFilterKey = '7d' | '15d' | '30d' | 'custom';
type CollectionLoadingKey = 'servicesLoading' | 'incidentsLoading' | 'maintenancesLoading';
import { parseApiDateAsUtc } from '../shared/models/status-time.utils';

@Component({
  selector: 'app-status-main-page',
  standalone: true,
  providers: [TitleCasePipe],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatNativeDateModule,
    MatTooltipModule,
    StatusPageRichTextPipe
  ],
  templateUrl: './status-main-page.component.html',
  styleUrls: [
    './status-main-page.component.scss',
    './status-main-page.component.history-controls.scss',
    './status-main-page.component.datepicker.scss',
    './status-main-page.component.cards-summary.scss',
    './status-main-page.component.expansion-fixes.scss',
    './status-main-page.component.layout-cards.scss',
    './status-main-page.component.cards-timeline.scss',
    './status-main-page.component.history-details.scss',
    './status-main-page.component.dark-theme.scss',
    './status-main-page.component.dark-responsive.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusMainPageComponent implements OnInit, OnDestroy {
  // ---------------------------------------------------------------------------
  // UI constants
  // ---------------------------------------------------------------------------
  readonly serviceSkeletonRows = [1, 2, 3];
  readonly incidentHistorySkeletonRows = [1, 2, 3, 4];
  readonly historyViewMode = 'history';
  private readonly customDateRangeLookbackMonths = 12;
  private readonly customDateRangeMaxDays = 30;
  private readonly incidentImpactClassMap: Record<number, string> = {
    1: 'resolved',
    2: 'monitoring',
    3: 'identified',
    4: 'investigating'
  };
  private readonly validImpactValues = new Set<number>([1, 2, 3, 4]);

  // ---------------------------------------------------------------------------
  // Branding and theme state
  // ---------------------------------------------------------------------------
  isDarkTheme = false;
  title = STATUS_MAIN_DEFAULT_TITLE;
  description = STATUS_MAIN_DEFAULT_DESCRIPTION;
  domainName = STATUS_MAIN_DEFAULT_DOMAIN;
  logoUrl: string | null = null;
  primaryColor = STATUS_MAIN_DEFAULT_PRIMARY_COLOR;
  overallStatus = STATUS_MAIN_DEFAULT_OVERALL_STATUS;

  // ---------------------------------------------------------------------------
  // Route and mode context
  // ---------------------------------------------------------------------------
  isInternalMode = false;
  isHistoryOnlyView = false;
  currentOrg = '';
  currentSlug = '';
  statusPageId: string | null = null;
  statusPageMeta: any = null;

  // ---------------------------------------------------------------------------
  // Subscription and publish state
  // ---------------------------------------------------------------------------
  allowSubscriptions = true;
  canAcceptSubscribers = true;
  isInternalPagePublished = true;
  enabledChannels: string[] = [];
  currentSubscriberCount: number | null = null;

  // ---------------------------------------------------------------------------
  // Data collections rendered by the view
  // ---------------------------------------------------------------------------
  services: ServiceItem[] = [];
  internalServiceGroups: GroupedServiceGroup[] = [];
  incidents: PublicIncidentItem[] = [];
  activeIncidents: PublicIncidentItem[] = [];
  incidentHistoryItems: PublicIncidentItem[] = [];
  filteredIncidentHistoryItems: PublicIncidentItem[] = [];
  maintenances: PublicMaintenanceItem[] = [];
  incidentHistoryCount = 0;

  // ---------------------------------------------------------------------------
  // Loading and transition flags
  // ---------------------------------------------------------------------------
  isPageInitLoading = true;
  servicesLoading = false;
  incidentsLoading = false;
  maintenancesLoading = false;
  isRedirectingToSso = false;
  isSsoLogoutInProgress = false;

  // ---------------------------------------------------------------------------
  // Incident history date filtering
  // ---------------------------------------------------------------------------
  selectedDateFilter: DateFilterKey | null = null;
  incidentHistorySearchQuery = '';
  customRange: { start: Date | null; end: Date | null } = { start: null, end: null };
  maxDate: Date = new Date();
  minDate: Date = dayjs().subtract(this.customDateRangeLookbackMonths, 'month').startOf('day').toDate();
  locale = 'en-US';
  private activeStartDate: string | null = null;
  private activeEndDate: string | null = null;
  private lastAppliedFilter: DateFilterKey | null = null;
  private appliedRange: { start: Date; end: Date } | null = null;

  @ViewChild('rangePicker') rangePicker?: MatDateRangePicker<Date>;
  @ViewChild('incidentHistorySearchInput') incidentHistorySearchInput?: ElementRef<HTMLInputElement>;

  // ---------------------------------------------------------------------------
  // SSO/session state
  // ---------------------------------------------------------------------------
  isSsoProtectedPage = false;
  isSsoSessionActive = false;
  ssoError = '';

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------
  internalError = '';
  publicError = '';

  // ---------------------------------------------------------------------------
  // Timezone preferences used by date rendering
  // ---------------------------------------------------------------------------
  profileTimeZoneName = '';
  profileTimeZone: string | null = null;
  publicDisplayTimeZone: string | null = null;

  // ---------------------------------------------------------------------------
  // One-time URL actions and route targeting
  // ---------------------------------------------------------------------------
  private hasProcessedSubscriptionTokenAction = false;
  private isPublicTokenActionInvalid = false;
  private requestedIncidentId = '';
  private requestedMaintenanceId = '';

  // ---------------------------------------------------------------------------
  // Browser storage keys
  // ---------------------------------------------------------------------------
  private readonly themeStorageKey = 'alertops-status-main-theme';
  private readonly publicStatusContextKey = 'public_status_context';
  private hasRedirectedDisabledInternalPage = false;
  private readonly destroy$ = new Subject<void>();
  private readonly collectionLoadSequence: Record<CollectionLoadingKey, number> = {
    servicesLoading: 0,
    incidentsLoading: 0,
    maintenancesLoading: 0
  };

  /**
   * Injects services used for routing, API calls, state management, and UI coordination.
   */
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly statusPageService: StatusPageService,
    private readonly statusPageCapabilitiesService: StatusPageCapabilitiesService,
    private readonly abilitiesService: AbilitiesService,
    private readonly authService: AuthService,
    private readonly authHttpService: AuthHTTPService,
    private readonly userProfileServices: UserProfileServices,
    private readonly _cd: ChangeDetectorRef,
    private readonly titleService: Title,
    private readonly titleCasePipe: TitleCasePipe
  ) {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(advancedFormat);
  }

  /**
   * Initializes component state, derives route context, and starts first-pass data loading.
   */
  ngOnInit(): void {
    this.initializeThemePreference();
    this.isHistoryOnlyView = this.resolveHistoryOnlyViewFlag();
    this.initializeIncidentHistoryDateFilter();
    this.isInternalMode = this.getRouteMode() === 'internal';
    this.captureRequestedEntityRoute();
    this.setDomainNameFromHost();

    if (this.isInternalMode) {
      this.watchInternalStatusPageCapability();
      this.loadInternalStatusPage();
      return;
    }

    const name = this.route.snapshot.queryParamMap.get('name');
    if (name) {
      this.title = name;
    }

    const [urlSubdomain, urlSlug] = getStatusRoutePartsFromUrl(this.router.url);
    this.currentOrg = urlSubdomain;
    this.currentSlug = urlSlug;
    const loadPublicPage = (): void => {
      this.tryProcessSubscriptionTokenActionFromUrl(urlSubdomain, urlSlug);
      this.processSsoTokenFromUrl();

      if (urlSubdomain && urlSlug) {
        this.loadPublicStatusPageMeta(urlSubdomain, urlSlug);
      } else {
        this.resolveAliasAndLoadPublicStatusPage();
      }
      this.markForView();
    };

    this.resolvePublicDisplayTimeZone()
      .pipe(take(1))
      .subscribe((displayTimeZone) => {
        this.applyResolvedPublicDisplayTimeZone(displayTimeZone);
        loadPublicPage();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Updates the browser tab title from the status page name.
   *
   * @param domainName - Display name used to build the tab title.
   */
  setPageTitle(domainName: string | null | undefined): void {
    if (domainName) {
      const formatted = this.titleCasePipe.transform(domainName);
      this.titleService.setTitle(`${formatted} | Alertops`);
    } else {
      this.titleService.setTitle('Alertops');
    }
  }

  /**
   * Returns the backend status text for display.
   */
  getStatusLabel(statusText?: string | null, statusCode?: number | string | null): string {
    const normalizedStatusText = String(statusText || '').trim();
    if (normalizedStatusText) {
      return normalizedStatusText;
    }
    return String(statusCode ?? '').trim();
  }

  /**
   * Returns status class from the current state.
   */
  getStatusClass(statusCodeOrText?: number | string | null, statusText?: string | null): string {
    const code = Number(statusCodeOrText);
    if (Number.isFinite(code)) {
      switch (code) {
        case 1:
          return 'operational';
        case 2:
          return 'degraded';
        case 3:
          return 'partial';
        case 4:
          return 'major';
        default:
          break;
      }
    }

    return mapStatusClass(String(statusText || statusCodeOrText || ''));
  }

  /**
   * Returns maintenance status label from the current state.
   */
  getMaintenanceStatusLabel(item: PublicMaintenanceItem): string {
    return mapMaintenanceStatusLabel(item);
  }

  /**
   * Returns incident status label from the current state.
   */
  getIncidentStatusLabel(item: PublicIncidentItem): string {
    return mapIncidentStatusLabel(item);
  }

  /**
   * Returns whether the incident history search currently has a value.
   */
  get hasActiveIncidentHistorySearch(): boolean {
    return !!this.normalizeIncidentHistorySearchText(this.incidentHistorySearchQuery);
  }

  /**
   * Returns incident history count display for template bindings and conditional rendering.
   */
  get incidentHistoryCountDisplay(): number {
    if (this.isHistoryOnlyView) {
      return this.incidentHistoryItems.length;
    }
    return this.incidentHistoryCount;
  }

  /**
   * Returns overall status class from the current state.
   */
  getOverallStatusClass(): string {
    const status = String(this.overallStatus || '').toLowerCase();
    if (!status) {
      return 'unknown';
    }
    if (status.includes('operational') || status.includes('ok')) {
      return 'operational';
    }
    if (status.includes('partial')) {
      return 'partial';
    }
    if (status.includes('major') || status.includes('outage') || status.includes('down')) {
      return 'major';
    }
    if (status.includes('degraded')) {
      return 'degraded';
    }
    if (status.includes('maintenance')) {
      return 'maintenance';
    }
    return 'unknown';
  }

  /**
   * Returns overall status icon from the current state.
   */
  getOverallStatusIcon(status: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('all systems operational') || s === 'operational' || s === 'all systems are operational') {
      return 'check_circle';
    }
    return 'cancel';
  }

  /**
   * Returns incident pill class from the current state.
   */
  getIncidentPillClass(item: PublicIncidentItem): string {
    return this.resolveIncidentStatusClass(item?.statusName || item?.status || '', true);
  }

  /**
   * Returns incident impact pill class from the current state.
   */
  getIncidentImpactClass(itemOrImpact: PublicIncidentItem | number | string | null | undefined): string {
    const rawImpact =
      typeof itemOrImpact === 'object' && itemOrImpact !== null
        ? itemOrImpact.impact
        : itemOrImpact;
    const impact = this.parseIncidentImpactCode(rawImpact);
    return impact !== null ? this.incidentImpactClassMap[impact] ?? 'monitoring' : 'monitoring';
  }

  /**
   * Resolves incident status class using fallback rules.
   */
  private resolveIncidentStatusClass(statusValue: string | number, includeOperational: boolean): string {
    const status = String(statusValue || '').toLowerCase();
    if (status.includes('resolved') || status.includes('complete')) {
      return 'resolved';
    }
    if (status.includes('identified')) {
      return 'identified';
    }
    if (status.includes('investigat')) {
      return 'investigating';
    }
    if (status.includes('monitor')) {
      return 'monitoring';
    }
    if (status.includes('major') || status.includes('outage')) {
      return 'major';
    }
    if (includeOperational && status.includes('operational')) {
      return 'operational';
    }
    return 'default';
  }

  /**
   * Parses a supported incident impact code from API data.
   */
  private parseIncidentImpactCode(rawImpact: unknown): number | null {
    if (typeof rawImpact === 'number' && Number.isInteger(rawImpact) && this.validImpactValues.has(rawImpact)) {
      return rawImpact;
    }

    if (typeof rawImpact === 'string') {
      const parsedImpact = Number(rawImpact.trim());
      if (Number.isInteger(parsedImpact) && this.validImpactValues.has(parsedImpact)) {
        return parsedImpact;
      }
    }

    return null;
  }

  /**
   * Checks whether the context has incident timeline.
   */
  hasIncidentTimeline(item: PublicIncidentItem): boolean {
    return this.getIncidentTimeline(item).length > 0;
  }

  /**
   * Returns incident timeline from the current state.
   */
  getIncidentTimeline(item: PublicIncidentItem): PublicIncidentUpdateItem[] {
    if (!Array.isArray(item?.updates)) {
      return [];
    }
    return item.updates;
  }

  /**
   * Handles incident history toggle from the UI.
   */
  onIncidentHistoryToggle(item: PublicIncidentItem, expanded: boolean): void {
    if (!item) {
      return;
    }
    const details = this.ensureIncidentHistoryDetails(item);
    details.expanded = expanded;
    item.expanded = expanded;
    if (expanded) {
      this.loadIncidentHistoryDetails(item);
    }
    this.markForView();
  }

  /**
   * Applies client-side search to the current date-filtered incident history collection.
   */
  onIncidentHistorySearchInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.incidentHistorySearchQuery = input?.value || '';
    this.refreshFilteredIncidentHistoryItems();
    this.markForView();
  }

  /**
   * Focuses the incident history search input when the search control chrome is clicked.
   */
  focusIncidentHistorySearchInput(): void {
    this.incidentHistorySearchInput?.nativeElement?.focus({ preventScroll: true });
  }

  /**
   * Clears client-side incident history search and restores the date-filtered collection.
   */
  clearIncidentHistorySearch(): void {
    if (!this.incidentHistorySearchQuery) {
      return;
    }
    this.incidentHistorySearchQuery = '';
    this.refreshFilteredIncidentHistoryItems();
    this.markForView();
  }

  /**
   * Applies a quick or custom date range to the incident history view.
   */
  setDateFilter(filter: DateFilterKey): void {
    this.refreshIncidentHistoryDateBounds();

    if (filter === 'custom') {
      this.customRange = this.getDefaultIncidentHistoryCustomRange();
      this.selectedDateFilter = filter;
      this.markForView();
      setTimeout(() => this.rangePicker?.open());
      return;
    }

    this.applyQuickRange(filter, true);
  }

  /**
   * Restricts custom incident-history ranges to the supported window.
   */
  customDateFilter = (date: Date | null): boolean => {
    if (!date) {
      return false;
    }

    const day = dayjs(date).startOf('day');
    const min = dayjs(this.minDate).startOf('day');
    const max = dayjs(this.maxDate).startOf('day');
    if (day.isBefore(min) || day.isAfter(max)) {
      return false;
    }

    if (!this.customRange.start || this.customRange.end) {
      return true;
    }

    const start = dayjs(this.customRange.start).startOf('day');
    const maxEnd = start.add(this.customDateRangeMaxDays - 1, 'day');
    const cappedMaxEnd = maxEnd.isAfter(max) ? max : maxEnd;
    return !day.isBefore(start) && !day.isAfter(cappedMaxEnd);
  };

  /**
   * Resets the custom end date when the user starts a new range.
   */
  onCustomStartDateChange(): void {
    if (this.selectedDateFilter === 'custom') {
      this.customRange.end = null;
    }
  }

  /**
   * Applies the selected custom range when the date picker closes.
   */
  applyCustomRange(): void {
    if (!this.customRange.start) {
      this.selectedDateFilter = this.lastAppliedFilter;
      this.markForView();
      return;
    }
    if (!this.customRange.end) {
      this.customRange.end = this.customRange.start;
    }
    const start = this.customRange.start;
    const end = this.customRange.end ?? start;
    const normalized = this.normalizeIncidentHistoryRange(start, end);
    this.applyDateRange(normalized.start, normalized.end, 'custom', true);
  }

  /**
   * Checks whether any incident-history detail request is still loading for the given incident.
   */
  isIncidentHistoryLoading(item: PublicIncidentItem): boolean {
    const details = item?.historyDetails;
    return !!(details?.timelineLoading || details?.postmortemLoading);
  }

  /**
   * Checks whether the flow should show incident postmortem.
   */
  shouldShowIncidentPostmortem(item: PublicIncidentItem): boolean {
    return this.shouldLoadPostmortem(item) || this.hasIncidentPostmortemContent(item?.historyDetails?.postmortem);
  }

  /**
   * Checks whether the context has postmortem value.
   */
  hasPostmortemValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return true;
  }

  /**
   * Checks whether a normalized postmortem has any displayable public content.
   */
  hasIncidentPostmortemContent(postmortem: PublicIncidentPostmortem | null | undefined): boolean {
    if (!postmortem) {
      return false;
    }

    return [
      postmortem.summary,
      postmortem.rootCause,
      postmortem.impact,
      postmortem.resolution,
      postmortem.lessonsLearned,
      postmortem.actionItems
    ].some((value) => this.hasPostmortemValue(value));
  }

  /**
   * Formats postmortem content for the UI.
   */
  formatPostmortemContent(value: any, fallback: string): string {
    if (Array.isArray(value)) {
      const items = value
        .map((entry) => normalizeStatusPageRichText(entry))
        .filter((entry) => !!entry);
      if (!items.length) {
        return fallback;
      }
      const listItems = items.map((entry) => `<li>${entry}</li>`).join('');
      return normalizeStatusPageRichTextWithFallback(`<ol>${listItems}</ol>`, fallback);
    }
    if (value === null || value === undefined) {
      return fallback;
    }
    if (typeof value === 'object') {
      return this.formatRichText(JSON.stringify(value), fallback);
    }
    return this.formatRichText(value, fallback);
  }

  /**
   * Returns timeline update class from the current state.
   */
  getTimelineUpdateClass(update: PublicIncidentUpdateItem): string {
    return this.resolveIncidentStatusClass(update?.statusName || update?.status || '', false);
  }

  /**
   * Returns relative time from the current state.
   */
  getRelativeTime(value: any): string {
    return this.formatRelativeTime(value, 'past');
  }

  /**
   * Returns relative maintenance time from the current state.
   */
  getMaintenanceRelativeTime(item: PublicMaintenanceItem): string {
    const startValue = item?.actualStartDateLocal || item?.scheduledStartDateLocal;
    const endValue = item?.actualEndDateLocal || item?.scheduledEndDateLocal || startValue;

    if (item?.isCompleted) {
      const label = this.formatRelativeTime(endValue, 'past');
      return label === 'time unavailable' ? 'Completed' : `Ended ${label}`;
    }

    if (item?.isActive) {
      const label = this.formatRelativeTime(startValue, 'past');
      return label === 'time unavailable' ? 'In progress' : `Started ${label}`;
    }

    const label = this.formatRelativeTime(startValue, 'future');
    return label === 'time unavailable' ? 'Starts soon' : `Starts ${label}`;
  }

  /**
   * Formats rich text for the UI.
   */
  formatRichText(value: any, fallback: string): string {
    const source = String(value ?? '').trim();
    if (!source) {
      return fallback;
    }
    return normalizeStatusPageRichTextWithFallback(source.replaceAll('\n', '<br>'), fallback);
  }

  /**
   * Returns tooltip text from the current state.
   */
  getTooltipText(value: any, fallback: string): string {
    const source = String(value ?? '').trim();
    if (!source) {
      return fallback;
    }
    const normalizedHtml = normalizeStatusPageRichTextWithFallback(source.replaceAll('\n', '<br>'), fallback);
    const parsed = new DOMParser().parseFromString(normalizedHtml, 'text/html');
    const text = String(parsed.body?.textContent || '').replaceAll(/\s+/g, ' ').trim();
    return text || fallback;
  }

  /**
   * Returns a stable identity key so Angular can track list items efficiently.
   */
  trackByMaintenanceId(index: number, item: PublicMaintenanceItem): number {
    return item?.maintenanceWindowId || index;
  }

  /**
   * Returns a stable identity key so Angular can track list items efficiently.
   */
  trackByIncidentId(index: number, item: PublicIncidentItem): number {
    return item?.incidentId || index;
  }

  /**
   * Returns a stable identity key so Angular can track list items efficiently.
   */
  trackByIncidentUpdateId(index: number, item: PublicIncidentUpdateItem): number {
    return item?.incidentUpdateId || index;
  }

  /**
   * Returns a stable identity key so Angular can track list items efficiently.
   */
  trackByServiceId(index: number, item: ServiceItem): number {
    return item?.serviceId || index;
  }

  /**
   * Returns a stable identity key so Angular can track list items efficiently.
   */
  trackByGroupId(index: number, item: { groupId?: number | string; name?: string } | null | undefined): number | string {
    return item?.groupId || item?.name || index;
  }

  /**
   * Returns a stable identity key so Angular can track list items efficiently.
   */
  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Returns a stable identity key so Angular can track list items efficiently.
   */
  trackByText(index: number, value: string): string | number {
    return value || index;
  }

  /**
   * Returns affected services label from the current state.
   */
  getAffectedServicesLabel(serviceIds: any): string {
    if (!Array.isArray(serviceIds) || !serviceIds.length) {
      return 'No affected services';
    }
    return serviceIds.length === 1 ? 'Affected: 1 service' : `Affected: ${serviceIds.length} services`;
  }

  /**
   * Returns incident affected service names from the current state.
   */
  getIncidentAffectedServiceNames(item: PublicIncidentItem): string[] {
    return this.buildAffectedServiceNames(
      item?.affectedServiceNames,
      item?.affectedServices,
      item?.affectedServiceIds
    );
  }

  /**
   * Returns incident impact label from the current state.
   */
  getIncidentImpactName(item: PublicIncidentItem): string {
    return String(item?.impactName || '').trim();
  }

  /**
   * Returns incident impact label for public incident cards.
   */
  getIncidentImpactLabel(item: PublicIncidentItem): string {
    const impactName = this.getIncidentImpactName(item);
    return impactName ? `Impact: ${impactName}` : '';
  }

  /**
   * Returns maintenance affected service names from the current state.
   */
  getMaintenanceAffectedServiceNames(item: PublicMaintenanceItem): string[] {
    return this.getIncidentAffectedServiceNames(item as unknown as PublicIncidentItem);
  }

  /**
   * Returns incident affected services label from the current state.
   */
  getIncidentAffectedServicesLabel(item: PublicIncidentItem): string {
    return this.buildAffectedServicesLabel(
      item?.affectedServiceNames,
      item?.affectedServices,
      item?.affectedServiceIds
    );
  }

  /**
   * Checks whether an incident should be treated as active for current-page grouping.
   */
  private isIncidentActive(item: PublicIncidentItem): boolean {
    if (typeof item?.isActive === 'boolean') {
      return item.isActive;
    }
    const status = String(item?.statusName || item?.status || '').toLowerCase();
    if (!status) {
      return true;
    }
    if (status.includes('resolved') || status.includes('complete') || status.includes('closed')) {
      return false;
    }
    return true;
  }

  /**
   * Returns service name by id from the current state.
   */
  private getServiceNameById(serviceId: number): string {
    const match = (this.services || []).find((service: ServiceItem) => Number(service?.serviceId) === Number(serviceId));
    return String(match?.name || '').trim();
  }

  /**
   * Builds affected services label for downstream calls.
   */
  private buildAffectedServicesLabel(
    affectedServiceNames: any,
    affectedServices: any,
    affectedServiceIds: any
  ): string {
    const resolvedNames = this.buildAffectedServiceNames(
      affectedServiceNames,
      affectedServices,
      affectedServiceIds
    );
    if (resolvedNames.length) {
      return `Affected: ${resolvedNames.join(', ')}`;
    }

    const ids = Array.isArray(affectedServiceIds) ? affectedServiceIds : [];
    if (!ids.length) {
      return 'Affected: None';
    }
    return this.getAffectedServicesLabel(ids);
  }

  /**
   * Builds affected service names for downstream calls.
   */
  private buildAffectedServiceNames(
    affectedServiceNames: any,
    affectedServices: any,
    affectedServiceIds: any
  ): string[] {
    const directNames = Array.isArray(affectedServiceNames)
      ? affectedServiceNames
          .map((name) => String(name || '').trim())
          .filter((name) => !!name)
      : [];
    if (directNames.length) {
      return directNames;
    }

    const mappedNames = Array.isArray(affectedServices)
      ? affectedServices
          .map((service) => String(service?.serviceName || '').trim())
          .filter((name) => !!name)
      : [];
    if (mappedNames.length) {
      return mappedNames;
    }

    const ids = Array.isArray(affectedServiceIds) ? affectedServiceIds : [];
    if (!ids.length) {
      return [];
    }

    return ids
      .map((id: number) => this.getServiceNameById(id))
      .filter((name) => !!name);
  }

  /**
   * Sets incidents and updates dependent fields.
   */
  private setIncidents(items: PublicIncidentItem[]): void {
    this.incidents = Array.isArray(items) ? items : [];
    this.activeIncidents = this.incidents.filter((item) => this.isIncidentActive(item));
    this.incidentHistoryItems = this.incidents.filter((item) => !this.isIncidentActive(item));
    this.activeIncidents.forEach((incident) => {
      if (typeof incident?.expanded !== 'boolean') {
        incident.expanded = false;
      }
    });
    if (this.isHistoryOnlyView) {
      this.initializeIncidentHistoryItems(this.incidents);
    }
    this.refreshFilteredIncidentHistoryItems();
  }

  /**
   * Sets the incident history-only collection without reclassifying the response as active incidents.
   */
  private setIncidentHistoryItems(items: PublicIncidentItem[]): void {
    this.incidents = Array.isArray(items) ? items : [];
    this.activeIncidents = [];
    this.incidentHistoryItems = this.incidents;
    this.initializeIncidentHistoryItems(this.incidentHistoryItems);
    this.refreshFilteredIncidentHistoryItems();
  }

  /**
   * Refreshes the client-side incident history search projection.
   */
  private refreshFilteredIncidentHistoryItems(): void {
    const query = this.normalizeIncidentHistorySearchText(this.incidentHistorySearchQuery);
    if (!query) {
      this.filteredIncidentHistoryItems = this.incidentHistoryItems;
      return;
    }

    this.filteredIncidentHistoryItems = this.incidentHistoryItems.filter((item) =>
      this.buildIncidentHistorySearchText(item).includes(query)
    );
  }

  /**
   * Builds normalized searchable text for an incident history row.
   */
  private buildIncidentHistorySearchText(item: PublicIncidentItem): string {
    const affectedServiceNames = this.getIncidentAffectedServiceNames(item);
    const values = [
      item?.title,
      this.getPlainTextForIncidentHistorySearch(item?.description),
      this.getIncidentStatusLabel(item),
      this.getIncidentImpactName(item),
      item?.displayStartedDate,
      item?.displayStartedTimeWithZone,
      item?.displayStartedDateTimeWithZone,
      item?.startedAt,
      item?.resolvedAt,
      ...affectedServiceNames
    ];

    return this.normalizeIncidentHistorySearchText(values.join(' '));
  }

  /**
   * Converts rich incident text into plain text for matching.
   */
  private getPlainTextForIncidentHistorySearch(value: any): string {
    const source = String(value ?? '').trim();
    if (!source) {
      return '';
    }

    const normalizedHtml = normalizeStatusPageRichText(source.replaceAll('\n', '<br>'));
    if (typeof DOMParser === 'undefined') {
      return normalizedHtml.replace(/<[^>]*>/g, ' ');
    }

    const parsed = new DOMParser().parseFromString(normalizedHtml, 'text/html');
    return String(parsed.body?.textContent || '').trim();
  }

  /**
   * Normalizes incident-history search text for case-insensitive matching.
   */
  private normalizeIncidentHistorySearchText(value: any): string {
    return String(value ?? '')
      .toLocaleLowerCase()
      .replaceAll(/\s+/g, ' ')
      .trim();
  }

  /**
   * Ensures incident history details exists before it is used.
   */
  private ensureIncidentHistoryDetails(item: PublicIncidentItem): IncidentHistoryDetailState {
    if (!item.historyDetails) {
      item.historyDetails = this.createIncidentHistoryDetailsState();
    }
    return item.historyDetails;
  }

  /**
   * Creates incident history details state for the current flow.
   */
  private createIncidentHistoryDetailsState(): IncidentHistoryDetailState {
    return {
      expanded: false,
      timelineLoading: false,
      postmortemLoading: false,
      timelineLoaded: false,
      postmortemLoaded: false,
      postmortem: null
    };
  }

  /**
   * Loads incident history details and applies it to local component state.
   */
  private loadIncidentHistoryDetails(item: PublicIncidentItem): void {
    const incidentId = item?.incidentId;
    if (!incidentId) {
      return;
    }

    const details = this.ensureIncidentHistoryDetails(item);
    const shouldLoadPostmortem = this.shouldLoadPostmortem(item);
    const timelineRequestNeeded = !details.timelineLoaded;
    const postmortemRequestNeeded = shouldLoadPostmortem && !details.postmortemLoaded;

    if (!timelineRequestNeeded && !postmortemRequestNeeded) {
      return;
    }

    if (timelineRequestNeeded) {
      details.timelineLoading = true;
    }
    if (postmortemRequestNeeded) {
      details.postmortemLoading = true;
    }
    this.markForView();

    const timeline$ = timelineRequestNeeded
      ? this.getIncidentHistoryIncidentRequest(incidentId).pipe(
          catchError(() => of(null))
        )
      : of(null);
    const postmortem$ = postmortemRequestNeeded
      ? this.getIncidentHistoryPostmortemRequest(incidentId).pipe(
          catchError(() => of(null))
        )
      : of(null);

    forkJoin({ timeline: timeline$, postmortem: postmortem$ })
      .pipe(
        finalize(() => {
          if (timelineRequestNeeded) {
            details.timelineLoading = false;
          }
          if (postmortemRequestNeeded) {
            details.postmortemLoading = false;
          }
          this.markForView();
        })
      )
      .subscribe(({ timeline, postmortem }) => {
        if (timelineRequestNeeded) {
          const normalized = timeline
            ? this.decorateIncidentsForView(
                normalizeIncidents(this.wrapPublicEntityResponseAsCollection(timeline, 'incident'))
              )
            : [];
          const resolvedIncident = normalized[0];
          if (resolvedIncident) {
            this.mergeIncidentHistoryDetail(item, resolvedIncident);
          }
          item.updates = resolvedIncident?.updates || [];
          details.timelineLoaded = true;
        }

        if (postmortemRequestNeeded) {
          const normalizedPostmortem = postmortem ? this.normalizePostmortemResponse(postmortem) : null;
          details.postmortem = normalizedPostmortem;
          if (this.hasIncidentPostmortemContent(normalizedPostmortem)) {
            item.hasPostmortem = true;
          }
          details.postmortemLoaded = true;
        }

        this.markForView();
      });
  }

  /**
   * Returns incident history incident request from the current state.
   */
  private getIncidentHistoryIncidentRequest(incidentId: number | string): Observable<any> {
    if (this.isInternalMode) {
      return this.statusPageService.getInternalIncidentBySlug(this.currentSlug, incidentId);
    }

    if (!this.currentOrg || !this.currentSlug) {
      return of(null);
    }

    const ssoToken = this.getSsoTokenForRequest();
    return this.statusPageService.getPublicIncidentByOrgSlug(this.currentOrg, this.currentSlug, incidentId, ssoToken);
  }

  /**
   * Applies richer lazy-loaded incident metadata to the history card item.
   */
  private mergeIncidentHistoryDetail(item: PublicIncidentItem, detail: PublicIncidentItem): void {
    const fields: (keyof PublicIncidentItem)[] = [
      'startedAt',
      'resolvedAt',
      'startedAtLocal',
      'resolvedAtLocal',
      'timezoneAbbreviation',
      'durationMinutes',
      'affectedServices',
      'affectedServiceNames',
      'affectedServiceIds',
      'impact',
      'impactName',
      'status',
      'statusName',
      'isActive',
      'hasPostmortem'
    ];

    fields.forEach((field) => {
      const value = detail[field];
      if (this.hasIncidentDetailValue(value)) {
        (item as any)[field] = value;
      }
    });

    const decorated = this.decorateIncidentForView(item);
    item.displayStartedDate = decorated.displayStartedDate;
    item.displayStartedTimeWithZone = decorated.displayStartedTimeWithZone;
    item.displayStartedDateTimeWithZone = decorated.displayStartedDateTimeWithZone;
  }

  /**
   * Checks whether a detail value should update the existing history item.
   */
  private hasIncidentDetailValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return true;
  }

  /**
   * Returns incident history postmortem request from the current state.
   */
  private getIncidentHistoryPostmortemRequest(incidentId: number | string): Observable<any> {
    if (this.isInternalMode) {
      return this.statusPageService.getInternalIncidentPostmortemBySlug(this.currentSlug, incidentId);
    }

    if (!this.currentOrg || !this.currentSlug) {
      return of(null);
    }

    const ssoToken = this.getSsoTokenForRequest();
    return this.statusPageService.getPublicIncidentPostmortemByOrgSlug(
      this.currentOrg,
      this.currentSlug,
      incidentId,
      ssoToken
    );
  }

  /**
   * Normalizes postmortem response into a consistent internal format.
   */
  private normalizePostmortemResponse(response: any): PublicIncidentPostmortem | null {
    if (!response) {
      return null;
    }

    const dataPayload =
      response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
        ? response.data
        : null;
    const source =
      dataPayload?.postmortem ||
      dataPayload?.postMortem ||
      dataPayload ||
      response?.postmortem ||
      response?.postMortem ||
      response;

    if (!source || typeof source !== 'object') {
      return null;
    }

    const normalized = {
      summary: source.summary ?? source.overview,
      rootCause: source.rootCause ?? source.root_cause,
      impact: source.impact,
      resolution: source.resolution ?? source.lessonsLearned ?? source.lessons_learned,
      lessonsLearned: source.lessonsLearned ?? source.lessons_learned,
      actionItems: source.actionItems ?? source.action_items
    };

    return this.hasIncidentPostmortemContent(normalized) ? normalized : null;
  }

  /**
   * Checks whether the flow should load postmortem.
   */
  private shouldLoadPostmortem(item: PublicIncidentItem): boolean {
    if (!item) {
      return false;
    }
    if (item.isIncident === false) {
      return false;
    }
    return item.hasPostmortem === true;
  }

  /**
   * Formats duration minutes for the UI.
   */
  formatDurationMinutes(minutes: any): string {
    const totalRaw = Number(minutes);
    if (!Number.isFinite(totalRaw) || totalRaw <= 0) {
      return 'Duration unavailable';
    }

    const totalMinutes = Math.max(1, Math.round(totalRaw));
    const days = Math.floor(totalMinutes / (24 * 60));
    if (days > 0) {
      return `${days} day${days === 1 ? '' : 's'}`;
    }

    const hours = Math.floor(totalMinutes / 60);
    if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'}`;
    }

    return `${totalMinutes} min${totalMinutes === 1 ? '' : 's'}`;
  }

  /**
   * Returns date time range from the current state.
   */
  getDateTimeRange(start: any, end: any): string {
    const startDate = parseApiDateAsUtc(start);
    const endDate = parseApiDateAsUtc(end);
    if (!startDate || Number.isNaN(startDate.getTime())) {
      return 'TBD';
    }
    const startTime = startDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (!endDate || Number.isNaN(endDate.getTime())) {
      return startTime;
    }
    const endTime = endDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${startTime} - ${endTime}`;
  }

  /**
   * Returns theme toggle label for template bindings and conditional rendering.
   */
  get themeToggleLabel(): string {
    return this.isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode';
  }

  /**
   * Toggles theme based on user intent.
   */
  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;
    this.persistThemePreference();
    this.removeThemeQueryParamFromUrl();
    this.markForView();
  }

  /**
   * Opens subscribe dialog for the user.
   */
  openSubscribeDialog(): void {
    if (this.isSubscribeButtonDisabled) {
      return;
    }

    const dialogThemeConfig = this.getSubscribeDialogThemeConfig();
    const limits = this.statusPageCapabilitiesService.limitsSnapshot;
    const maxSubscribers = this.isInternalMode
      ? limits?.maxSubscribersPerInternalPage ?? null
      : limits?.maxSubscribersPerExternalPage ?? null;

    const dialogConfig = {
      width: '640px',
      maxWidth: '92vw',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: dialogThemeConfig.panelClass,
      data: {
        org: this.currentOrg,
        slug: this.currentSlug,
        statusPageId: this.statusPageId || this.statusPageMeta?.statusPageId || undefined,
        enabledChannels: this.enabledChannels,
        availableServices: this.services,
        availableServiceGroups: this.internalServiceGroups,
        pageMode: this.isInternalMode ? 'internal' : 'public',
        ssoToken: this.getSsoTokenForRequest(),
        maxSubscribers,
        currentSubscribers: this.currentSubscriberCount,
        mode: 'subscribe' as const,
        isDarkTheme: dialogThemeConfig.isDarkTheme,
        primaryColor: this.primaryColor
      }
    };
    if (this.isInternalMode) {
      this.dialog.open(StatusMainPageInternalSubscriptionDialogComponent, dialogConfig);
      return;
    }

    this.dialog.open(StatusMainPagePublicSubscriptionDialogComponent, dialogConfig);
  }

  /**
   * Returns show subscribe button for template bindings and conditional rendering.
   */
  get showSubscribeButton(): boolean {
    if (!this.allowSubscriptions || this.isHistoryOnlyView) {
      return false;
    }
    return Array.isArray(this.enabledChannels) && this.enabledChannels.length > 0;
  }

  /**
   * Returns whether subscribe action should be disabled for template bindings.
   */
  get isSubscribeButtonDisabled(): boolean {
    return !this.canAcceptSubscribers || (this.isInternalMode && !this.isInternalPagePublished);
  }

  /**
   * Indicates whether the internal services payload contains standalone services.
   */
  get hasStandaloneServices(): boolean {
    return (this.services || []).some((service) => !service?.groups || service?.groups?.length === 0);
  }

  /**
   * Returns tooltip text for disabled subscribe action.
   */
  get subscribeButtonTooltip(): string {
    if (!this.canAcceptSubscribers) {
      return 'Subscriptions are unavailable for this status page.';
    }
    if (this.isSubscribeButtonDisabled) {
      return 'Subscriptions are unavailable because this internal status page has not been published yet.';
    }
    return '';
  }

  /**
   * Returns incident history tab url for template bindings and conditional rendering.
   */
  get incidentHistoryTabUrl(): string {
    const win = this.getWindow();
    if (!win?.location?.href) {
      return '';
    }
    try {
      const url = new URL(win.location.href);
      this.clearSideEffectQueryParams(url);
      url.searchParams.set('view', this.historyViewMode);
      return url.toString();
    } catch {
      return '';
    }
  }

  /**
   * Returns status overview url for history-only navigation.
   */
  get statusOverviewUrl(): string {
    const win = this.getWindow();
    if (!win?.location?.href) {
      return '';
    }
    try {
      const url = new URL(win.location.href);
      this.clearSideEffectQueryParams(url);
      url.searchParams.delete('view');
      return url.toString();
    } catch {
      return '';
    }
  }

  /**
   * Returns route mode from the current state.
   */
  private getRouteMode(): StatusMainPageMode {
    const mode = String(this.route.snapshot.data?.['mode'] || '').toLowerCase();
    return mode === 'internal' ? 'internal' : 'public';
  }

  /**
   * Captures requested entity route for targeted data loading.
   */
  private captureRequestedEntityRoute(): void {
    this.requestedIncidentId = String(this.route.snapshot.paramMap.get('incidentId') || '').trim();
    this.requestedMaintenanceId = String(this.route.snapshot.paramMap.get('maintenanceId') || '').trim();
  }

  /**
   * Resolves history only view flag using fallback rules.
   */
  private resolveHistoryOnlyViewFlag(): boolean {
    const view = String(this.route.snapshot.queryParamMap.get('view') || '').trim().toLowerCase();
    return view === this.historyViewMode;
  }

  /**
   * Returns current host from the current state.
   */
  private getCurrentHost(): string {
    const win = this.getWindow();
    if (!win?.location?.hostname) {
      return '';
    }
    return String(win.location.hostname || '').toLowerCase();
  }

  /**
   * Returns normalized current path segments from the browser location.
   */
  private getCurrentPathSegments(): string[] {
    const win = this.getWindow();
    const path = String(win?.location?.pathname || '').trim();
    if (!path) {
      return [];
    }
    return path.split('/').filter((segment) => !!segment);
  }

  /**
   * Sets domain name from host and updates dependent fields.
   */
  private setDomainNameFromHost(): void {
    this.domainName = resolveDomainNameFromHost(this.getCurrentHost()) || STATUS_MAIN_DEFAULT_DOMAIN;
  }

  /**
   * Sets empty collections and updates dependent fields.
   */
  private setEmptyCollections(): void {
    this.services = [];
    this.internalServiceGroups = [];
    this.setIncidents([]);
    this.maintenances = [];
    this.incidentHistoryCount = 0;
    this.servicesLoading = false;
    this.incidentsLoading = false;
    this.maintenancesLoading = false;
    this.currentSubscriberCount = null;
  }

  /**
   * Loads internal status page and applies it to local component state.
   */
  private loadInternalStatusPage(): void {
    const slug = String(this.route.snapshot.paramMap.get('slug') || '').trim();
    this.currentOrg = String(this.route.parent?.snapshot.paramMap.get('subdomain') || '').trim();
    this.currentSlug = slug;
    this.internalError = '';
    this.isSsoProtectedPage = false;
    this.isSsoSessionActive = true;
    this.isRedirectingToSso = false;
    this.ssoError = '';
    this.isInternalPagePublished = true;

    if (!slug) {
      this.isPageInitLoading = false;
      this.internalError = STATUS_MAIN_ERROR_MESSAGES.INTERNAL_NOT_RESOLVED;
      this.setEmptyCollections();
      this.markForView();
      return;
    }

    if (this.redirectDisabledInternalPageToDashboard()) {
      this.isPageInitLoading = false;
      this.setEmptyCollections();
      this.markForView();
      return;
    }

    this.isPageInitLoading = true;
    forkJoin({
      statusPage: this.statusPageService.getInternalStatusPageBySlug(slug),
      profileTimeZone: this.resolveProfileTimeZone(true)
    })
      .pipe(
        finalize(() => {
          this.isPageInitLoading = false;
          this.markForView();
        })
      )
      .subscribe({
        next: ({ statusPage, profileTimeZone }) => {
          this.applyResolvedProfileTimeZone(profileTimeZone);
          this.applyInternalStatusPageMeta(statusPage);
          this.loadGroupedServices(
            this.statusPageService.getInternalServicesBySlug(slug, { grouped: true }),
            true
          );
          this.loadIncidents(
            this.getInternalIncidentsRequest(slug),
            true
          );
          if (!this.isHistoryOnlyView) {
            this.loadIncidentHistoryCount(
              this.statusPageService.getInternalIncidentsBySlug(slug),
              true
            );
          }
          this.loadMaintenances(
            this.statusPageService.getInternalUpcomingMaintenanceBySlug(slug, 30),
            true
          );
        },
        error: (error: HttpErrorResponse) => {
          this.setEmptyCollections();

          if (error?.status === 404) {
            this.router.navigate(['/error/404']);
            return;
          }
          if (this.redirectDisabledInternalPageToDashboard()) {
            return;
          }
          if (error?.status === 401 || error?.status === 403) {
            this.internalError = STATUS_MAIN_ERROR_MESSAGES.INTERNAL_NO_ACCESS;
            return;
          }
          this.internalError =
            error?.error?.detail ||
            error?.error?.message ||
            STATUS_MAIN_ERROR_MESSAGES.INTERNAL_LOAD_FAILED;
        }
      });
  }

  /**
   * Applies internal status page meta to component state.
   */
  private applyInternalStatusPageMeta(response: any): void {
    this.statusPageMeta = response || null;
    this.statusPageId = this.extractStatusPageId(response) || this.statusPageId || '';
    this.currentSlug = String(response?.pageSlug || response?.slug || this.currentSlug || '').trim();
    this.title = response?.name || response?.displayName || this.title;
    this.description = response?.description || this.description;
    this.logoUrl = response?.logoUrl || null;
    this.primaryColor = response?.primaryColor || STATUS_MAIN_DEFAULT_PRIMARY_COLOR;
    this.overallStatus =
      response?.overallStatus ||
      response?.statusDescription ||
      this.overallStatus ||
      STATUS_MAIN_DEFAULT_OVERALL_STATUS;
    this.allowSubscriptions = response?.allowSubscriptions !== false;
    this.canAcceptSubscribers = response?.canAcceptSubscribers !== false;
    this.enabledChannels = this.extractEnabledChannels(response);
    this.currentSubscriberCount = this.extractSubscriberCount(response);
    this.isInternalPagePublished = this.extractIsPublished(response);
    this.markForView();
  }

  /**
   * Loads public status page meta and applies it to local component state.
   */
  private loadPublicStatusPageMeta(org: string, slug: string): void {
    this.isPageInitLoading = true;
    this.publicError = '';
    const ssoToken = this.getSsoTokenForRequest();
    this.statusPageService
      .getPublicStatusPageByOrgSlug(org, slug, ssoToken)
      .pipe(
        finalize(() => {
          this.isPageInitLoading = false;
          this.markForView();
        })
      )
      .subscribe({
        next: (response: any) => {
          if (this.isPublicTokenActionInvalid) {
            return;
          }
          if (this.isSsoAuthChallengeResponse(response)) {
            this.handleSsoRequiredResponse(response);
            return;
          }
          this.applyPublicStatusPageMeta(response);
          this.persistPublicStatusContext(org, slug);
          this.publicError = '';
          this.isSsoProtectedPage = false;
          this.isSsoSessionActive = true;
          this.clearSsoCallbackParamsFromUrl();
          this.resolvePublicDisplayTimeZoneFromStatusPage(response)
            .pipe(take(1))
            .subscribe((displayTimeZone) => {
              if (displayTimeZone) {
                this.applyResolvedPublicDisplayTimeZone(displayTimeZone);
              }
              this.loadServicesForCurrentPage(org, slug, response);
            });
        },
        error: (error: HttpErrorResponse) => {
          if (this.isPublicTokenActionInvalid) {
            return;
          }
          const errorPayload = error?.error;
          if (this.isSsoRequiredResponse(errorPayload)) {
            const payload = errorPayload && typeof errorPayload === 'object'
              ? errorPayload
              : { message: 'SSO authentication required' };
            this.handleSsoRequiredResponse(payload);
            return;
          }

          if (this.isPublicNotFoundError(error)) {
            this.applyPublicErrorState(this.resolvePublicNotFoundMessage(error));
            return;
          }

          this.statusPageMeta = null;
          this.isSsoProtectedPage = false;
          this.isSsoSessionActive = true;
          this.loadServicesForCurrentPage(org, slug, null);
        }
      });
  }

  /**
   * Resolves alias and load public status page using fallback rules.
   */
  private resolveAliasAndLoadPublicStatusPage(): void {
    const aliasCandidates = getAliasCandidatesFromHost(this.getCurrentHost());
    if (!aliasCandidates.length) {
      this.isPageInitLoading = false;
      this.setEmptyCollections();
      this.applyPublicErrorState(STATUS_MAIN_ERROR_MESSAGES.PUBLIC_NOT_FOUND);
      return;
    }

    this.isPageInitLoading = true;
    this.ssoError = '';
    this.publicError = '';
    const ssoToken = this.getSsoTokenForRequest();
    this.tryResolveAlias(aliasCandidates, ssoToken, 0);
    this.markForView();
  }

  /**
   * Attempts to resolve alias with graceful fallbacks.
   */
  private tryResolveAlias(aliasCandidates: string[], ssoToken: string, index: number): void {
    if (index >= aliasCandidates.length) {
      this.applyPublicErrorState(STATUS_MAIN_ERROR_MESSAGES.PUBLIC_NOT_FOUND);
      return;
    }

    const alias = aliasCandidates[index];
    this.statusPageService.getPublicStatusPageByAlias(alias, ssoToken).subscribe({
      next: (response: any) => {
        if (!this.applyResolvedAliasResponse(response)) {
          this.tryResolveAlias(aliasCandidates, ssoToken, index + 1);
        }
      },
      error: (error: HttpErrorResponse) => {
        if (this.isPublicNotFoundError(error) && index < aliasCandidates.length - 1) {
          this.tryResolveAlias(aliasCandidates, ssoToken, index + 1);
          return;
        }
        if (this.isPublicNotFoundError(error)) {
          this.applyPublicErrorState(this.resolvePublicNotFoundMessage(error));
          return;
        }
        this.applyPublicErrorState(
          this.resolvePublicErrorMessage(error, STATUS_MAIN_ERROR_MESSAGES.ALIAS_NOT_RESOLVED)
        );
      }
    });
  }

  /**
   * Applies resolved alias response to component state.
   */
  private applyResolvedAliasResponse(response: any): boolean {
    const resolved = this.extractOrgSlugFromAliasResponse(response);
    if (!resolved.org || !resolved.slug) {
      return false;
    }

    this.currentOrg = resolved.org;
    this.currentSlug = resolved.slug;
    this.persistPublicStatusContext(resolved.org, resolved.slug);
    this.tryProcessSubscriptionTokenActionFromUrl(resolved.org, resolved.slug);
    this.loadPublicStatusPageMeta(resolved.org, resolved.slug);
    return true;
  }

  /**
   * Extracts org slug from alias response from API payloads.
   */
  private extractOrgSlugFromAliasResponse(response: any): AliasResolutionResult {
    return extractOrgSlugFromAliasResponse(response);
  }

  /**
   * Loads services for current page and applies it to local component state.
   */
  private loadServicesForCurrentPage(urlSubdomain: string, urlSlug: string, pageMeta: any): void {
    if (urlSubdomain && urlSlug) {
      const ssoToken = this.getSsoTokenForRequest();
      this.loadGroupedServices(
        this.statusPageService.getPublicServicesByOrgSlug(urlSubdomain, urlSlug, { grouped: true }, ssoToken)
      );
      if (this.requestedIncidentId) {
        this.loadFocusedIncidentByOrgSlug(urlSubdomain, urlSlug, this.requestedIncidentId, ssoToken);
      } else {
        this.loadIncidents(this.getPublicIncidentsRequestByOrgSlug(urlSubdomain, urlSlug, ssoToken));
        if (!this.isHistoryOnlyView) {
          this.loadIncidentHistoryCount(
            this.statusPageService.getPublicIncidentsByOrgSlug(urlSubdomain, urlSlug, undefined, ssoToken)
          );
        }
      }
      if (this.requestedMaintenanceId) {
        this.loadFocusedMaintenanceByOrgSlug(urlSubdomain, urlSlug, this.requestedMaintenanceId, ssoToken);
      } else {
        this.loadMaintenances(
          this.statusPageService.getPublicUpcomingMaintenanceByOrgSlug(urlSubdomain, urlSlug, ssoToken)
        );
      }
      return;
    }

    const fallbackSlug = String(pageMeta?.pageSlug || '').trim();
    if (fallbackSlug) {
      const ssoToken = this.getSsoTokenForRequest();
      this.currentSlug = fallbackSlug;
      this.loadGroupedServices(
        this.statusPageService.getPublicServicesBySlug(fallbackSlug, { grouped: true }, ssoToken)
      );
      this.loadIncidents(this.getPublicIncidentsRequestBySlug(fallbackSlug, ssoToken));
      if (!this.isHistoryOnlyView) {
        this.loadIncidentHistoryCount(
          this.statusPageService.getPublicIncidentsBySlug(fallbackSlug, undefined, ssoToken)
        );
      }
      this.loadMaintenances(this.statusPageService.getPublicUpcomingMaintenanceBySlug(fallbackSlug, ssoToken));
      return;
    }

    this.setEmptyCollections();
    this.markForView();
  }

  /**
   * Runs collection load with shared success/error handling.
   */
  private runCollectionLoad<T>(
    loadingKey: CollectionLoadingKey,
    request$: Observable<T>,
    onSuccess: (response: T) => void,
    onError?: (error: HttpErrorResponse) => void
  ): void {
    const loadSequence = ++this.collectionLoadSequence[loadingKey];
    this[loadingKey] = true;
    this.markForView();

    const isCurrentLoad = (): boolean => this.collectionLoadSequence[loadingKey] === loadSequence;
    let finished = false;
    const finishLoad = (): void => {
      if (finished || !isCurrentLoad()) {
        return;
      }
      finished = true;
      this[loadingKey] = false;
      this.markForView();
    };

    request$
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (!isCurrentLoad()) {
            return;
          }
          try {
            onSuccess(response);
          } finally {
            finishLoad();
          }
        },
        error: (error: HttpErrorResponse) => {
          if (!isCurrentLoad()) {
            return;
          }
          try {
            if (onError) {
              onError(error);
            }
          } finally {
            finishLoad();
          }
        },
        complete: () => {
          finishLoad();
        }
      });
  }

  /**
   * Loads grouped services and applies it to local component state.
   */
  private loadGroupedServices(request$: Observable<any>, internalAccessAware: boolean = false): void {
    this.runCollectionLoad(
      'servicesLoading',
      request$,
      (response: any) => {
        this.internalServiceGroups = normalizeGroupedServices(response);
        this.services = normalizeGroupedServiceItems(response);
      },
      (error: HttpErrorResponse) => {
        this.internalServiceGroups = [];
        this.services = [];
        if (internalAccessAware) {
          this.applyInternalAccessError(error);
        }
      }
    );
  }

  /**
   * Loads services and applies it to local component state.
   */
  private loadServices(request$: Observable<any>, internalAccessAware: boolean = false): void {
    this.runCollectionLoad(
      'servicesLoading',
      request$,
      (response: any) => {
        this.services = response?.data || [];
      },
      (error: HttpErrorResponse) => {
        this.services = [];
        if (internalAccessAware) {
          this.applyInternalAccessError(error);
        }
      }
    );
  }

  /**
   * Loads incidents and applies it to local component state.
   */
  private loadIncidents(request$: Observable<any>, internalAccessAware: boolean = false): void {
    this.runCollectionLoad(
      'incidentsLoading',
      request$,
      (response: any) => {
        const normalized = this.decorateIncidentsForView(normalizeIncidents(response));
        if (this.isHistoryOnlyView) {
          this.setIncidentHistoryItems(normalized.filter((item) => !this.isIncidentActive(item)));
          this.incidentHistoryCount = this.incidentHistoryItems.length;
          return;
        }
        this.setIncidents(normalized);
      },
      (error: HttpErrorResponse) => {
        if (this.isHistoryOnlyView) {
          this.setIncidentHistoryItems([]);
          this.incidentHistoryCount = 0;
        } else {
          this.setIncidents([]);
        }
        if (internalAccessAware) {
          this.applyInternalAccessError(error);
        }
      }
    );
  }

  /**
   * Initializes incident history items before rendering.
   */
  private initializeIncidentHistoryItems(items: PublicIncidentItem[]): void {
    (items || []).forEach((incident) => {
      if (!incident.historyDetails) {
        incident.historyDetails = this.createIncidentHistoryDetailsState();
      }
    });
  }

  /**
   * Initializes the default incident history date range without triggering a reload.
   */
  private initializeIncidentHistoryDateFilter(): void {
    if (!this.isHistoryOnlyView) {
      return;
    }
    this.locale = this.resolveDateLocale();
    this.refreshIncidentHistoryDateBounds();
    this.applyQuickRange('30d', false);
  }

  /**
   * Returns the default custom picker range used when entering custom mode from quick filters.
   */
  private getDefaultIncidentHistoryCustomRange(): { start: Date; end: Date } {
    const today = this.getPickerToday();
    return {
      start: today.subtract(this.customDateRangeMaxDays - 1, 'day').toDate(),
      end: today.toDate()
    };
  }

  /**
   * Applies a quick incident history range.
   */
  private applyQuickRange(filter: DateFilterKey, reload: boolean): void {
    const daysMap: Record<DateFilterKey, number> = {
      '7d': 7,
      '15d': 15,
      '30d': 30,
      'custom': 30
    };
    const days = daysMap[filter] || 30;
    const today = this.getPickerToday();
    const start = today.subtract(days - 1, 'day').startOf('day');
    const end = today.endOf('day');
    this.applyDateRange(start, end, filter, reload);
  }

  /**
   * Stores a date range and optionally reloads the incident history collection.
   */
  private applyDateRange(start: Dayjs, end: Dayjs, filter: DateFilterKey, reload: boolean): void {
    const newStart = start.format('YYYY-MM-DD');
    const newEnd = end.format('YYYY-MM-DD');
    const isSameRange = this.activeStartDate === newStart && this.activeEndDate === newEnd;

    this.activeStartDate = newStart;
    this.activeEndDate = newEnd;
    this.appliedRange = { start: start.toDate(), end: end.toDate() };
    this.customRange = { start: start.toDate(), end: end.toDate() };
    this.selectedDateFilter = filter;
    this.lastAppliedFilter = filter;

    if (!reload || isSameRange) {
      this.markForView();
      return;
    }

    this.reloadIncidentHistory();
  }

  /**
   * Normalizes custom incident history ranges to supported date bounds.
   */
  private normalizeIncidentHistoryRange(startDate: Date, endDate: Date): { start: Dayjs; end: Dayjs } {
    const today = this.getPickerToday();
    const now = today.endOf('day');
    const minAllowed = today.subtract(this.customDateRangeLookbackMonths, 'month').startOf('day');
    let start = dayjs(startDate).startOf('day');
    let end = dayjs(endDate).endOf('day');

    if (start.isBefore(minAllowed)) {
      start = minAllowed;
    }
    if (start.isAfter(now)) {
      start = now.startOf('day');
    }
    if (end.isBefore(minAllowed)) {
      end = minAllowed.endOf('day');
    }
    if (end.isAfter(now)) {
      end = now;
    }
    if (end.isBefore(start)) {
      end = start.endOf('day');
    }

    const maxEnd = start.add(this.customDateRangeMaxDays - 1, 'day').endOf('day');
    if (end.isAfter(maxEnd)) {
      end = maxEnd;
    }
    if (end.isAfter(now)) {
      end = now;
    }

    return { start, end };
  }

  /**
   * Reloads only the incident history list after a date filter change.
   */
  private reloadIncidentHistory(): void {
    if (!this.isHistoryOnlyView || !this.currentSlug) {
      this.markForView();
      return;
    }

    const ssoToken = this.getSsoTokenForRequest();
    if (this.isInternalMode) {
      this.loadIncidents(this.getInternalIncidentsRequest(this.currentSlug), true);
      return;
    }

    if (this.currentOrg) {
      this.loadIncidents(this.getPublicIncidentsRequestByOrgSlug(this.currentOrg, this.currentSlug, ssoToken));
      return;
    }

    this.loadIncidents(this.getPublicIncidentsRequestBySlug(this.currentSlug, ssoToken));
  }

  /**
   * Returns query params for incident history date filtering.
   */
  private getIncidentHistoryDateOptions(): { startDate?: string | null; endDate?: string | null } {
    return {
      startDate: this.activeStartDate,
      endDate: this.activeEndDate
    };
  }

  /**
   * Updates custom date picker bounds.
   */
  private refreshIncidentHistoryDateBounds(): void {
    const today = this.getPickerToday();
    this.maxDate = today.toDate();
    this.minDate = today.subtract(this.customDateRangeLookbackMonths, 'month').toDate();
  }

  /**
   * Returns today's date in the active browser locale.
   */
  private getPickerToday(): Dayjs {
    return dayjs().locale(this.locale || 'en').startOf('day');
  }

  /**
   * Resolves the browser locale used for date range display.
   */
  private resolveDateLocale(): string {
    const nav = this.getWindow()?.navigator;
    return String(nav?.language || 'en-US').trim() || 'en-US';
  }

  /**
   * Loads focused incident by org slug and applies it to local component state.
   */
  private loadFocusedIncidentByOrgSlug(
    org: string,
    slug: string,
    incidentId: string,
    ssoToken: string
  ): void {
    this.runCollectionLoad(
      'incidentsLoading',
      this.statusPageService.getPublicIncidentByOrgSlug(org, slug, incidentId, ssoToken),
      (response: any) => {
        const normalized = this.decorateIncidentsForView(
          normalizeIncidents(
            this.wrapPublicEntityResponseAsCollection(response, 'incident')
          )
        );
        const targetIncident = normalized[0];
        if (!targetIncident) {
          this.setIncidents([]);
          this.incidentHistoryCount = 0;
          return;
        }

        this.isHistoryOnlyView = !this.isIncidentActive(targetIncident);
        this.setIncidents(normalized);
        this.incidentHistoryCount = this.isHistoryOnlyView ? this.incidentHistoryItems.length : 0;
      },
      () => {
        this.setIncidents([]);
        this.incidentHistoryCount = 0;
      }
    );
  }

  /**
   * Loads incident history count and applies it to local component state.
   */
  private loadIncidentHistoryCount(request$: Observable<any>, internalAccessAware: boolean = false): void {
    request$.subscribe({
      next: (response: any) => {
        const normalized = normalizeIncidents(response);
        this.incidentHistoryCount = normalized.filter((item) => !this.isIncidentActive(item)).length;
        this.markForView();
      },
      error: (error: HttpErrorResponse) => {
        this.incidentHistoryCount = 0;
        if (internalAccessAware) {
          this.applyInternalAccessError(error);
        }
        this.markForView();
      }
    });
  }

  /**
   * Returns internal incidents request from the current state.
   */
  private getInternalIncidentsRequest(slug: string): Observable<any> {
    if (this.isHistoryOnlyView) {
      return this.statusPageService.getInternalIncidentsBySlug(slug, this.getIncidentHistoryDateOptions());
    }
    return this.statusPageService.getInternalActiveIncidentsBySlug(slug);
  }

  /**
   * Returns public incidents request by org slug from the current state.
   */
  private getPublicIncidentsRequestByOrgSlug(org: string, slug: string, ssoToken: string): Observable<any> {
    if (this.isHistoryOnlyView) {
      return this.statusPageService.getPublicIncidentsByOrgSlug(
        org,
        slug,
        this.getIncidentHistoryDateOptions(),
        ssoToken
      );
    }
    return this.statusPageService.getPublicActiveIncidentsByOrgSlug(org, slug, ssoToken);
  }

  /**
   * Returns public incidents request by slug from the current state.
   */
  private getPublicIncidentsRequestBySlug(slug: string, ssoToken: string): Observable<any> {
    if (this.isHistoryOnlyView) {
      return this.statusPageService.getPublicIncidentsBySlug(slug, this.getIncidentHistoryDateOptions(), ssoToken);
    }
    return this.statusPageService.getPublicActiveIncidentsBySlug(slug, ssoToken);
  }

  /**
   * Loads focused maintenance by org slug and applies it to local component state.
   */
  private loadFocusedMaintenanceByOrgSlug(
    org: string,
    slug: string,
    maintenanceId: string,
    ssoToken: string
  ): void {
    this.runCollectionLoad(
      'maintenancesLoading',
      this.statusPageService.getPublicMaintenanceWindowByOrgSlug(org, slug, maintenanceId, ssoToken),
      (response: any) => {
        this.maintenances = this.decorateMaintenancesForView(
          normalizeMaintenances(
            this.wrapPublicEntityResponseAsCollection(response, 'maintenance')
          )
        );
      },
      () => {
        this.maintenances = [];
      }
    );
  }

  /**
   * Loads maintenances and applies it to local component state.
   */
  private loadMaintenances(request$: Observable<any>, internalAccessAware: boolean = false): void {
    this.runCollectionLoad(
      'maintenancesLoading',
      request$,
      (response: any) => {
        this.maintenances = this.decorateMaintenancesForView(normalizeMaintenances(response));
      },
      (error: HttpErrorResponse) => {
        this.maintenances = [];
        if (internalAccessAware) {
          this.applyInternalAccessError(error);
        }
      }
    );
  }

  /**
   * Resolves the current user's profile timezone from existing auth/profile state.
   */
  private resolveProfileTimeZone(useFreshUserProfile: boolean = false): Observable<string | null> {
    const token = this.authService.getToken();
    if (!token) {
      return of(null);
    }

    const resolveCachedProfileTimeZone = (): Observable<string | null> => {
      const cachedProfileTimeZoneName = this.getCachedProfileTimeZoneName();
      if (!cachedProfileTimeZoneName) {
        return of(null);
      }
      return this.resolveProfileTimeZoneFromName(token, cachedProfileTimeZoneName).pipe(
        catchError(() => of(null))
      );
    };

    const currentUserId = this.getCurrentUserId();
    if (useFreshUserProfile && currentUserId) {
      return this.authService.getUserById(token, currentUserId).pipe(
        take(1),
        switchMap((profile: any) => {
          const profileTimeZoneName = String(profile?.time_zone || profile?.timeZone || '').trim();
          if (!profileTimeZoneName) {
            return resolveCachedProfileTimeZone();
          }
          this.cacheProfileTimeZoneName(profileTimeZoneName);
          return this.resolveProfileTimeZoneFromName(token, profileTimeZoneName);
        }),
        catchError(() => resolveCachedProfileTimeZone())
      );
    }

    const cachedProfileTimeZoneName = this.getCachedProfileTimeZoneName();
    if (cachedProfileTimeZoneName) {
      return this.resolveProfileTimeZoneFromName(token, cachedProfileTimeZoneName).pipe(
        catchError(() => of(null))
      );
    }

    if (!currentUserId) {
      return of(null);
    }

    return this.authService.getUserById(token, currentUserId).pipe(
      take(1),
      switchMap((profile: any) => {
        const profileTimeZoneName = String(profile?.time_zone || profile?.timeZone || '').trim();
        this.cacheProfileTimeZoneName(profileTimeZoneName);
        return this.resolveProfileTimeZoneFromName(token, profileTimeZoneName);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Resolves browser timezone first, then falls back to profile timezone, then UTC.
   */
  private resolvePublicDisplayTimeZone(): Observable<string> {
    const browserTimeZone = this.resolveBrowserTimeZone();
    if (browserTimeZone) {
      return of(browserTimeZone);
    }

    return this.resolveProfileTimeZone().pipe(
      map((profileTimeZone) => profileTimeZone || 'UTC'),
      catchError(() => of('UTC'))
    );
  }

  /**
   * Resolves the public status page timezone id into an IANA timezone.
   */
  private resolvePublicDisplayTimeZoneFromStatusPage(response: any): Observable<string | null> {
    const timeZoneId = this.extractStatusPageTimezoneId(response);
    if (!timeZoneId) {
      return of(null);
    }

    return this.resolveTimeZoneFromId(timeZoneId).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Extracts the timezone id from public status page metadata.
   */
  private extractStatusPageTimezoneId(response: any): string {
    return String(response?.timezoneId ?? response?.timezone_id ?? '').trim();
  }

  /**
   * Resolves a timezone catalog id into the timezone zone value used by dayjs.
   */
  private resolveTimeZoneFromId(timeZoneId: string): Observable<string | null> {
    const normalizedTimeZoneId = String(timeZoneId || '').trim();
    if (!normalizedTimeZoneId) {
      return of(null);
    }

    return this.authHttpService.getTimeZoneList().pipe(
      take(1),
      map((response: any) => {
        const catalog = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const matchedTimeZone = catalog.find((entry: any) =>
          String(entry?.id ?? '').trim() === normalizedTimeZoneId
        );
        const resolvedZone = String(matchedTimeZone?.zone || '').trim();
        return this.isValidIanaTimeZone(resolvedZone) ? resolvedZone : null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Resolves browser/device timezone from standard runtime APIs.
   */
  private resolveBrowserTimeZone(): string | null {
    try {
      if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
        return null;
      }

      const browserTimeZone = String(Intl.DateTimeFormat().resolvedOptions().timeZone || '').trim();
      return this.isValidIanaTimeZone(browserTimeZone) ? browserTimeZone : null;
    } catch {
      return null;
    }
  }

  /**
   * Resolves an IANA timezone from the stored profile timezone name.
   */
  private resolveProfileTimeZoneFromName(token: string, profileTimeZoneName: string): Observable<string | null> {
    const normalizedName = String(profileTimeZoneName || '').trim();
    if (!normalizedName) {
      return of(null);
    }

    this.profileTimeZoneName = normalizedName;
    if (this.isValidIanaTimeZone(normalizedName)) {
      return of(normalizedName);
    }

    return this.userProfileServices.timeZone(token).pipe(
      take(1),
      map((catalog: any[]) => {
        const matchedTimeZone = Array.isArray(catalog)
          ? catalog.find((entry: any) => entry?.name === normalizedName)
          : undefined;
        const resolvedZone = String(matchedTimeZone?.zone || '').trim();
        return this.isValidIanaTimeZone(resolvedZone) ? resolvedZone : null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Applies the resolved profile timezone and refreshes current display labels.
   */
  private applyResolvedProfileTimeZone(profileTimeZone: string | null): void {
    this.profileTimeZone = profileTimeZone;
    this.setIncidents(this.decorateIncidentsForView(this.incidents));
    this.maintenances = this.decorateMaintenancesForView(this.maintenances);
    this.markForView();
  }

  /**
   * Applies resolved public/private display timezone and refreshes current display labels.
   */
  private applyResolvedPublicDisplayTimeZone(displayTimeZone: string | null): void {
    this.publicDisplayTimeZone =
      displayTimeZone && this.isValidIanaTimeZone(displayTimeZone) ? displayTimeZone : 'UTC';
    this.setIncidents(this.decorateIncidentsForView(this.incidents));
    this.maintenances = this.decorateMaintenancesForView(this.maintenances);
    this.markForView();
  }

  /**
   * Returns cached profile timezone name from current auth state or local storage.
   */
  private getCachedProfileTimeZoneName(): string {
    const currentUser: any = this.authService.currentUserValue;
    const authValue = String(currentUser?.time_zone || currentUser?.timeZone || '').trim();
    if (authValue) {
      return authValue;
    }

    try {
      const stored = JSON.parse(localStorage.getItem('userLocalStorageToken') || 'null');
      return String(stored?.time_zone || stored?.timeZone || '').trim();
    } catch {
      return '';
    }
  }

  /**
   * Stores profile timezone name locally so subsequent internal-page loads can reuse it.
   */
  private cacheProfileTimeZoneName(profileTimeZoneName: string): void {
    const normalizedName = String(profileTimeZoneName || '').trim();
    if (!normalizedName) {
      return;
    }

    this.profileTimeZoneName = normalizedName;

    try {
      const stored = JSON.parse(localStorage.getItem('userLocalStorageToken') || 'null');
      if (!stored || typeof stored !== 'object') {
        return;
      }
      if (stored.time_zone === normalizedName) {
        return;
      }
      localStorage.setItem(
        'userLocalStorageToken',
        JSON.stringify({ ...stored, time_zone: normalizedName })
      );
    } catch {
      // Ignore malformed storage values and continue with in-memory state.
    }
  }

  /**
   * Returns current user id from auth state or local storage.
   */
  private getCurrentUserId(): string {
    const currentUser: any = this.authService.currentUserValue;
    const authUserId = currentUser?.user_id ?? currentUser?.userId;
    if (authUserId !== undefined && authUserId !== null && String(authUserId).trim()) {
      return String(authUserId).trim();
    }

    try {
      const stored = JSON.parse(localStorage.getItem('userLocalStorageToken') || 'null');
      const storedUserId = stored?.user_id ?? stored?.userId;
      return storedUserId !== undefined && storedUserId !== null ? String(storedUserId).trim() : '';
    } catch {
      return '';
    }
  }

  /**
   * Checks whether the supplied value is a valid IANA timezone.
   */
  private isValidIanaTimeZone(value: string | null | undefined): value is string {
    if (!value) {
      return false;
    }

    try {
      dayjs().tz(value);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Applies incident display labels from API-local date/time fields.
   */
  private decorateIncidentsForView(items: PublicIncidentItem[]): PublicIncidentItem[] {
    return (items || []).map((item) => this.decorateIncidentForView(item));
  }

  /**
   * Applies incident display labels from API-local date/time fields.
   */
  private decorateIncidentForView(item: PublicIncidentItem): PublicIncidentItem {
    const startedAtDisplay = this.buildDisplayLocalTimeLabel(
      item?.startedAtLocal,
      item?.timezoneAbbreviation
    );
    return {
      ...item,
      displayStartedDate: startedAtDisplay?.dateLabel || '',
      displayStartedTimeWithZone: startedAtDisplay?.timeWithZoneLabel || '',
      displayStartedDateTimeWithZone: startedAtDisplay?.dateTimeLabel || '',
      updates: this.decorateIncidentUpdatesForView(item?.updates || [])
    };
  }

  /**
   * Applies incident update display labels from API-local date/time fields.
   */
  private decorateIncidentUpdatesForView(items: PublicIncidentUpdateItem[]): PublicIncidentUpdateItem[] {
    return (items || []).map((item) => {
      const timestampDisplay = this.buildDisplayLocalTimeLabel(
        item?.createdDateLocal,
        item?.timezoneAbbreviation
      );
      return {
        ...item,
        displayTimestampWithZone: timestampDisplay?.dateTimeLabel || ''
      };
    });
  }

  /**
   * Applies maintenance display labels from API-local date/time fields.
   */
  private decorateMaintenancesForView(items: PublicMaintenanceItem[]): PublicMaintenanceItem[] {
    return (items || []).map((item, index) => ({
      ...item,
      expanded: typeof item?.expanded === 'boolean' ? item.expanded : index === 0,
      displayScheduledDate: this.buildDisplayDateRangeLabel(
        item?.scheduledStartDateLocal,
        item?.scheduledEndDateLocal,
        item?.timezoneAbbreviation
      ),
      displayScheduledTimeRange: this.buildDisplayTimeRangeLabel(
        item?.scheduledStartDateLocal,
        item?.scheduledEndDateLocal,
        item?.timezoneAbbreviation
      )
    }));
  }

  /**
   * Builds a formatted date-range label from API-local date/time fields.
   */
  private buildDisplayDateRangeLabel(start: any, end: any, timeZoneAbbreviation?: string | null): string {
    const startDisplay = this.buildDisplayLocalTimeLabel(start, timeZoneAbbreviation);
    if (!startDisplay) {
      return '';
    }

    const endDisplay = this.buildDisplayLocalTimeLabel(end, timeZoneAbbreviation);
    if (!endDisplay || startDisplay.dateLabel === endDisplay.dateLabel) {
      return startDisplay.dateLabel;
    }

    return `${startDisplay.dateLabel} - ${endDisplay.dateLabel}`;
  }

  /**
   * Builds a formatted time-range label from API-local date/time fields.
   */
  private buildDisplayTimeRangeLabel(start: any, end: any, timeZoneAbbreviation?: string | null): string {
    const startDisplay = this.buildDisplayLocalTimeLabel(start, timeZoneAbbreviation);
    if (!startDisplay) {
      return '';
    }

    const endDisplay = this.buildDisplayLocalTimeLabel(end, timeZoneAbbreviation);
    if (!endDisplay) {
      return startDisplay.timeWithZoneLabel;
    }

    if (startDisplay.timeZoneLabel === endDisplay.timeZoneLabel) {
      return `${startDisplay.timeLabel} - ${endDisplay.timeLabel} ${startDisplay.timeZoneLabel}`.trim();
    }

    return `${startDisplay.timeWithZoneLabel} - ${endDisplay.timeWithZoneLabel}`;
  }

  /**
   * Builds formatted date/time labels from an API-local source value without timezone conversion.
   */
  private buildDisplayLocalTimeLabel(value: any, timeZoneAbbreviation?: string | null): StatusTimeDisplay | null {
    if (!value) {
      return null;
    }

    const localValue = this.parseApiLocalDateTime(value);
    if (!localValue) {
      return null;
    }

    const timeZoneLabel = String(timeZoneAbbreviation || '').trim();
    const dateLabel = localValue.format('MMM D, YYYY');
    const timeLabel = localValue.format('h:mm A');

    return {
      dateLabel,
      timeLabel,
      dateTimeLabel: `${dateLabel} ${timeLabel} ${timeZoneLabel}`.trim(),
      timeWithZoneLabel: `${timeLabel} ${timeZoneLabel}`.trim(),
      timeZoneLabel
    };
  }

  /**
   * Parses an API-local date string as wall-clock time and ignores any offset suffix.
   */
  private parseApiLocalDateTime(value: any): Dayjs | null {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
      return null;
    }

    const withoutOffset = normalized.replace(/([zZ]|[+-]\d{2}:?\d{2})$/, '');
    const parsed = dayjs(withoutOffset);
    return parsed.isValid() ? parsed : null;
  }

  /**
   * Builds formatted date/time labels from a UTC source value.
   */
  private buildDisplayTimeLabel(value: any): StatusTimeDisplay | null {
    if (!value) {
      return null;
    }

    const zonedValue = this.toRenderedTimeZoneValue(value);
    if (!zonedValue) {
      return null;
    }

    const timeZoneLabel = this.buildTimeZoneLabel(
      zonedValue,
      this.isInternalMode ? 'short' : 'full'
    );
    const dateLabel = zonedValue.format('MMM D, YYYY');
    const timeLabel = zonedValue.format('h:mm A');

    return {
      dateLabel,
      timeLabel,
      dateTimeLabel: `${dateLabel} ${timeLabel} ${timeZoneLabel}`.trim(),
      timeWithZoneLabel: `${timeLabel} ${timeZoneLabel}`.trim(),
      timeZoneLabel
    };
  }

  /**
   * Converts a UTC timestamp into the active page timezone.
   */
  private toRenderedTimeZoneValue(value: any): Dayjs | null {
    if (!value) {
      return null;
    }

    if (this.isInternalMode) {
      if (!this.profileTimeZone) {
        return null;
      }

      const internalValue = dayjs.utc(value).tz(this.profileTimeZone);
      return internalValue.isValid() ? internalValue : null;
    }

    const zonedValue = dayjs.utc(value).tz(this.getPublicDisplayTimeZone());
    return zonedValue.isValid() ? zonedValue : null;
  }

  /**
   * Builds inline timezone label using the converted timestamp offset.
   */
  private buildTimeZoneLabel(value: Dayjs, format: 'short' | 'full' = 'short'): string {
    const abbreviation = String(value.format('z') || '').trim();
    const offsetLabel = `UTC${value.format('Z')}`;

    if (format === 'short') {
      return abbreviation || offsetLabel;
    }

    return abbreviation ? `${abbreviation} (${offsetLabel})` : `(${offsetLabel})`;
  }

  /**
   * Formats relative time text using the active page comparison timezone.
   */
  private formatRelativeTime(value: any, direction: 'past' | 'future' | 'auto' = 'auto'): string {
    const targetDate = this.resolveRelativeReferenceDate(value);
    if (!targetDate) {
      return 'time unavailable';
    }

    const now = this.getRelativeComparisonNow();
    const resolvedDirection = this.resolveRelativeDirection(targetDate, now, direction);

    const earlier = resolvedDirection === 'past' ? targetDate : now;
    const later = resolvedDirection === 'past' ? now : targetDate;
    const diffSeconds = Math.max(later.diff(earlier, 'second'), 0);
    const calendarDayDiff = Math.max(later.startOf('day').diff(earlier.startOf('day'), 'day'), 0);

    if (diffSeconds < 60) {
      return this.formatRelativeLessThan('minute', resolvedDirection);
    }

    if (calendarDayDiff >= 30) {
      const months = Math.floor(calendarDayDiff / 30);
      return this.formatRelativeQuantity(months, 'month', resolvedDirection);
    }

    if (calendarDayDiff >= 7) {
      const weeks = Math.floor(calendarDayDiff / 7);
      return this.formatRelativeQuantity(weeks, 'week', resolvedDirection);
    }

    if (calendarDayDiff >= 1) {
      return this.formatRelativeQuantity(calendarDayDiff, 'day', resolvedDirection);
    }

    if (diffSeconds < 3600) {
      return this.formatRelativeLessThan('hour', resolvedDirection);
    }

    return this.formatRelativeLessThan('day', resolvedDirection);
  }

  private resolveRelativeDirection(
    targetDate: Dayjs,
    now: Dayjs,
    direction: 'past' | 'future' | 'auto'
  ): 'past' | 'future' {
    if (direction !== 'auto') {
      return direction;
    }

    return targetDate.isAfter(now) ? 'future' : 'past';
  }

  private formatRelativeQuantity(
    value: number,
    unit: 'month' | 'week' | 'day',
    direction: 'past' | 'future'
  ): string {
    const suffix = value > 1 ? 's' : '';

    if (direction === 'future') {
      return `in about ${value} ${unit}${suffix}`;
    }

    return `about ${value} ${unit}${suffix} ago`;
  }

  private formatRelativeLessThan(unit: 'minute' | 'hour' | 'day', direction: 'past' | 'future'): string {
    if (direction === 'future') {
      return `in less than a ${unit}`;
    }

    return `less than a ${unit} ago`;
  }

  /**
   * Resolves the current comparison time in the same timezone as rendered values.
   */
  private getRelativeComparisonNow(): Dayjs {
    if (this.isInternalMode) {
      return this.profileTimeZone ? dayjs().tz(this.profileTimeZone) : dayjs();
    }

    return dayjs().tz(this.getPublicDisplayTimeZone());
  }

  /**
   * Resolves a UTC source timestamp into the comparison timezone.
   */
  private resolveRelativeReferenceDate(value: any): Dayjs | null {
    if (!value) {
      return null;
    }

    const utcDate = dayjs.utc(value);
    if (!utcDate.isValid()) {
      return null;
    }

    if (this.isInternalMode) {
      return this.profileTimeZone ? utcDate.tz(this.profileTimeZone) : utcDate.local();
    }

    return utcDate.tz(this.getPublicDisplayTimeZone());
  }

  /**
   * Returns resolved public/private display timezone with UTC as final fallback.
   */
  private getPublicDisplayTimeZone(): string {
    return this.publicDisplayTimeZone || 'UTC';
  }

  /**
   * Wraps public entity response as collection into a collection-like shape for shared mappers.
   */
  private wrapPublicEntityResponseAsCollection(
    response: any,
    entityType: 'incident' | 'maintenance'
  ): any {
    const dataPayload =
      response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
        ? response.data
        : {};

    const entity = this.extractPublicEntityFromResponse(response, entityType);
    const collectionKey = entityType === 'incident' ? 'incidents' : 'maintenanceWindows';

    return {
      ...response,
      data: {
        ...dataPayload,
        [collectionKey]: entity ? [entity] : []
      }
    };
  }

  /**
   * Extracts public entity from response from API payloads.
   */
  private extractPublicEntityFromResponse(
    response: any,
    entityType: 'incident' | 'maintenance'
  ): any {
    const dataPayload =
      response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
        ? response.data
        : null;

    if (entityType === 'incident') {
      return (
        dataPayload?.incident ||
        dataPayload?.item ||
        dataPayload ||
        response?.incident ||
        response?.item ||
        response
      );
    }

    return (
      dataPayload?.maintenanceWindow ||
      dataPayload?.maintenance ||
      dataPayload?.item ||
      dataPayload ||
      response?.maintenanceWindow ||
      response?.maintenance ||
      response?.item ||
      response
    );
  }

  /**
   * Applies internal access error to component state.
   */
  private applyInternalAccessError(error: HttpErrorResponse): void {
    if (this.redirectDisabledInternalPageToDashboard()) {
      return;
    }
    if (error?.status === 401 || error?.status === 403) {
      this.internalError = this.internalError || STATUS_MAIN_ERROR_MESSAGES.INTERNAL_NO_ACCESS;
    }
  }

  /**
   * Watches internal status page capability updates so refresh-time auth/me races still redirect.
   */
  private watchInternalStatusPageCapability(): void {
    this.statusPageCapabilitiesService.capabilities$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.redirectDisabledInternalPageToDashboard()) {
          this.setEmptyCollections();
          this.internalError = '';
          this.isPageInitLoading = false;
          this.markForView();
        }
      });

    this.abilitiesService.authMeContext$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.redirectDisabledInternalPageToDashboard()) {
          this.setEmptyCollections();
          this.internalError = '';
          this.isPageInitLoading = false;
          this.markForView();
        }
      });
  }

  /**
   * Redirects internal status page viewers without page-view access back to the app dashboard.
   */
  private redirectDisabledInternalPageToDashboard(): boolean {
    if (!this.isInternalMode || this.hasRedirectedDisabledInternalPage) {
      return false;
    }

    if (!this.isInternalStatusPageViewDenied()) {
      return false;
    }

    const account = this.resolveDashboardAccount();
    const commands = account ? ['/', account, 'dashboard'] : ['/', 'dashboard'];
    this.hasRedirectedDisabledInternalPage = true;
    this.router.navigate(commands, { replaceUrl: true });
    return true;
  }

  /**
   * Resolves whether auth/me marks internal status page viewing unavailable.
   */
  private isInternalStatusPageViewDenied(): boolean {
    if (this.statusPageCapabilitiesService.snapshot?.canViewPage === false) {
      return true;
    }

    return this.abilitiesService.authMeContextSnapshot?.capabilities?.statusPage?.canViewPage === false;
  }

  /**
   * Resolves account slug for internal dashboard redirects.
   */
  private resolveDashboardAccount(): string {
    const candidates = [
      this.currentOrg,
      this.route.parent?.snapshot?.paramMap?.get('subdomain'),
      this.getAccountFromStorage(),
      this.extractAccountFromRouterUrl()
    ];

    for (const candidate of candidates) {
      const value = String(candidate || '').trim().toLowerCase();
      if (value) {
        return value;
      }
    }

    return '';
  }

  /**
   * Resolves account slug from persisted login context.
   */
  private getAccountFromStorage(): string {
    const raw = localStorage.getItem('token-key');
    if (!raw) {
      return '';
    }

    try {
      const parsed = JSON.parse(raw);
      return String(parsed?.account || '').trim().toLowerCase();
    } catch {
      return '';
    }
  }

  /**
   * Resolves account slug from the current router url.
   */
  private extractAccountFromRouterUrl(): string {
    const url = String(this.router.url || '').split(/[?#]/)[0];
    const segments = url.split('/').filter(Boolean);
    return String(segments[0] || '').trim().toLowerCase();
  }

  /**
   * Applies public error state and clears stale page data for downstream handling.
   */
  private applyPublicErrorState(message: string): void {
    this.publicError = String(message || '').trim() || STATUS_MAIN_ERROR_MESSAGES.PUBLIC_NOT_FOUND;
    this.statusPageMeta = null;
    this.statusPageId = null;
    this.isPageInitLoading = false;
    this.isRedirectingToSso = false;
    this.isSsoProtectedPage = false;
    this.isSsoSessionActive = true;
    this.ssoError = '';
    this.setEmptyCollections();
    this.markForView();
  }

  /**
   * Checks whether a public API error represents a missing or unverified status page.
   */
  private isPublicNotFoundError(error: HttpErrorResponse): boolean {
    if (error?.status === 404) {
      return true;
    }
    const message = this.resolvePublicErrorMessage(error, '').toLowerCase();
    return (
      message.includes('no status page found') ||
      message.includes('not found') ||
      message.includes('not published') ||
      message.includes('not verified') ||
      message.includes('unverified')
    );
  }

  /**
   * Returns user-friendly message for missing or unverified public status pages.
   */
  private resolvePublicNotFoundMessage(_error?: HttpErrorResponse): string {
    return STATUS_MAIN_ERROR_MESSAGES.PUBLIC_NOT_FOUND;
  }

  /**
   * Returns public API error message from payloads with fallback support.
   */
  private resolvePublicErrorMessage(error: HttpErrorResponse, fallback: string): string {
    return String(error?.error?.message || error?.error?.detail || fallback || '').trim();
  }

  // =========================================================
  // SSO
  // =========================================================

  /**
   * Returns show sso logout button for template bindings and conditional rendering.
   */
  get showSsoLogoutButton(): boolean {
    if (this.isInternalMode || !this.isSsoSessionActive) {
      return false;
    }
    const visibilityType = String(
      this.statusPageMeta?.visibilityType || this.statusPageMeta?.visibility_type || ''
    ).toLowerCase();
    const isSsoEnabledPage =
      ['private_sso', 'public_sso', 'public_with_sso', 'publicwithsso'].includes(visibilityType) ||
      !!this.statusPageMeta?.requiresSsoAuthentication ||
      !!this.statusPageMeta?.requires_sso_authentication;
    if (!isSsoEnabledPage) {
      return false;
    }
    return !!this.getSsoTokenForRequest();
  }

  /**
   * Handles sso logout from the UI.
   */
  onSsoLogout(): void {
    if (!this.showSsoLogoutButton || this.isSsoLogoutInProgress) {
      return;
    }
    const statusPageId = this.getKnownStatusPageId();
    const pageReturnUrl = this.getSsoLogoutReturnUrl();

    this.clearStoredSsoToken();
    this.isSsoSessionActive = false;
    this.isSsoLogoutInProgress = true;
    this.isRedirectingToSso = true;
    this.ssoError = '';
    this.markForView();
    const win = this.getWindow();
    if (!win) {
      this.isSsoLogoutInProgress = false;
      this.isRedirectingToSso = false;
      this.markForView();
      return;
    }
    if (statusPageId) {
      const logoutUrl = this.statusPageService.getStatusPageSsoLogoutUrl(statusPageId, pageReturnUrl);
      win.location.replace(logoutUrl);
      return;
    }
    win.location.replace(pageReturnUrl);
  }

  /**
   * Handles sso login restart from the UI.
   */
  onSsoLogin(): void {
    if (this.isRedirectingToSso) {
      return;
    }
    this.isSsoProtectedPage = true;
    this.isSsoSessionActive = false;
    this.ssoError = '';
    this.redirectToSsoLogin(true);
  }

  // ---------- SSO Challenge Detection ----------

  /**
   * Checks whether a backend response indicates SSO authentication is required.
   */
  private isSsoRequiredResponse(response: any): boolean {
    if (!response) {
      return false;
    }
    if (typeof response === 'string') {
      return response.toLowerCase().includes('sso authentication required');
    }
    if (typeof response !== 'object') {
      return false;
    }
    const visibilityType = String(response?.visibilityType || response?.visibility_type || '').toLowerCase();
    const message = String(response?.message || '').toLowerCase();
    const loginUrl = String(response?.login_url || response?.loginUrl || '').trim();
    return (
      ['private_sso', 'private', 'public_sso', 'public_with_sso', 'publicwithsso'].includes(visibilityType) ||
      !!response?.requiresSsoAuthentication ||
      message.includes('sso authentication required') ||
      !!loginUrl
    );
  }

  /**
   * Checks whether a response payload is an SSO auth challenge payload.
   */
  private isSsoAuthChallengeResponse(response: any): boolean {
    if (!response || typeof response !== 'object') {
      return false;
    }
    const message = String(response?.message || '').toLowerCase();
    const loginUrl = String(response?.login_url || '').trim();
    return !!loginUrl || message.includes('sso authentication required');
  }

  /**
   * Handles sso required response and updates state accordingly.
   */
  private handleSsoRequiredResponse(response: any): void {
    this.isSsoProtectedPage = true;
    this.isSsoSessionActive = false;
    const loginUrl = String(response?.login_url || '').trim();
    this.statusPageId = this.extractStatusPageId(response) || this.extractStatusPageIdFromLoginUrl(loginUrl) || this.statusPageId || '';
    this.clearStoredSsoToken();
    this.ssoError = '';
    this.redirectToSsoLogin(true);
  }

  // ---------- SSO Redirect & Token Helpers ----------

  /**
   * Applies public status page meta to component state.
   */
  private applyPublicStatusPageMeta(response: any): void {
    this.statusPageMeta = response || null;
    this.statusPageId = this.extractStatusPageId(response) || this.statusPageId || '';
    this.title = response?.name || response?.displayName || this.title;
    this.description = response?.description || this.description;
    this.logoUrl = response?.logoUrl || response?.branding?.logoUrl || null;
    this.primaryColor = response?.primaryColor || response?.branding?.primaryColor || STATUS_MAIN_DEFAULT_PRIMARY_COLOR;
    this.overallStatus = response?.overallStatus || STATUS_MAIN_DEFAULT_OVERALL_STATUS;
    this.allowSubscriptions = response?.allowSubscriptions !== false;
    this.canAcceptSubscribers = response?.canAcceptSubscribers !== false;
    this.enabledChannels = this.extractEnabledChannels(response);
    this.currentSubscriberCount = this.extractSubscriberCount(response);
    this.markForView();
    this.setPageTitle(this.title);
  }

  /**
   * Returns clean return url from the current state.
   */
  private getCleanReturnUrl(): string {
    const win = this.getWindow();
    if (!win?.location?.href) {
      return '';
    }
    const url = new URL(win.location.href);
    url.searchParams.delete('sso_token');
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    return url.toString();
  }

  /**
   * Returns sso logout return url from the current state.
   */
  private getSsoLogoutReturnUrl(): string {
    const pageUrl = this.getCleanReturnUrl();
    if (pageUrl) {
      return pageUrl;
    }
    const win = this.getWindow();
    if (win?.location?.href) {
      return win.location.href;
    }
    return '/';
  }

  /**
   * Returns known status page id from the current state.
   */
  private getKnownStatusPageId(): string {
    return (
      this.extractStatusPageId(this.statusPageMeta) ||
      String(this.statusPageId || '').trim()
    );
  }

  /**
   * Redirects to sso login to the expected destination.
   */
  private redirectToSsoLogin(forceAuthn: boolean = false): void {
    const returnUrl = this.getCleanReturnUrl();
    const statusPageId =
      this.statusPageId ||
      this.statusPageMeta?.statusPageId ||
      this.statusPageMeta?.status_page_id;
    if (statusPageId) {
      this.isRedirectingToSso = true;
      const win = this.getWindow();
      if (win) {
        win.location.href = this.statusPageService.getStatusPageSsoLoginUrl(statusPageId, returnUrl, { forceAuthn });
        return;
      }
      this.isRedirectingToSso = false;
      this.ssoError = STATUS_MAIN_ERROR_MESSAGES.SSO_REDIRECT_FAILED;
      this.markForView();
      return;
    }
    this.isRedirectingToSso = false;
    this.ssoError = STATUS_MAIN_ERROR_MESSAGES.SSO_REDIRECT_FAILED;
    this.markForView();
  }

  /**
   * Extracts status page id from login url from API payloads.
   */
  private extractStatusPageIdFromLoginUrl(loginUrl: string): string {
    if (!loginUrl) {
      return '';
    }
    const match = /\/sso\/status_pages\/(\d+)\/login/i.exec(loginUrl);
    return match?.[1] || '';
  }

  /**
   * Extracts status page id from API payloads.
   */
  private extractStatusPageId(response: any): string {
    return String(
      response?.statusPageId ||
        response?.status_page_id ||
        response?.pageId ||
        response?.page_id ||
        response?.id ||
        response?.data?.statusPageId ||
        response?.data?.status_page_id ||
        response?.data?.pageId ||
        response?.data?.page_id ||
        response?.data?.id ||
        response?.statusPage?.id ||
        response?.data?.statusPage?.id ||
        ''
    ).trim();
  }

  /**
   * Extracts subscriber count from API payloads.
   */
  private extractSubscriberCount(response: any): number | null {
    const candidates = [
      response?.subscriberCount,
      response?.subscriber_count,
      response?.subscribers,
      response?.totalSubscribers,
      response?.total_subscribers,
      response?.subscriberTotal,
      response?.data?.subscriberCount,
      response?.data?.subscriber_count,
      response?.data?.subscribers,
      response?.data?.totalSubscribers,
      response?.data?.total_subscribers
    ];

    for (const value of candidates) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return null;
  }

  /**
   * Extracts enabled channels from API payloads.
   */
  private extractEnabledChannels(response: any): string[] {
    const candidates = [
      response?.enabledChannels,
      response?.data?.enabledChannels,
      response?.statusPage?.enabledChannels,
      response?.data?.statusPage?.enabledChannels
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate
          .map((value) => String(value ?? '').trim())
          .filter((value) => !!value);
      }
    }

    return [];
  }

  /**
   * Extracts published state from API payloads.
   */
  private extractIsPublished(response: any): boolean {
    const candidates = [
      response?.isPublished,
      response?.is_published,
      response?.published,
      response?.statusPage?.isPublished,
      response?.statusPage?.is_published,
      response?.statusPage?.published,
      response?.data?.isPublished,
      response?.data?.is_published,
      response?.data?.published,
      response?.data?.statusPage?.isPublished,
      response?.data?.statusPage?.is_published,
      response?.data?.statusPage?.published
    ];

    for (const value of candidates) {
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') {
          return true;
        }
        if (normalized === 'false') {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Persists public status context in browser storage when available.
   */
  private persistPublicStatusContext(org: string, slug: string): void {
    const win = this.getWindow();
    if (!win) {
      return;
    }
    const cleanOrg = String(org || '').trim().toLowerCase();
    const cleanSlug = String(slug || '').trim().toLowerCase();
    const host = String(win.location?.hostname || '').trim().toLowerCase();
    if (!cleanOrg || !cleanSlug || !host) {
      return;
    }
    try {
      win.localStorage.setItem(
        this.publicStatusContextKey,
        JSON.stringify({ org: cleanOrg, slug: cleanSlug, host })
      );
    } catch {
    }
  }

  /**
   * Processes sso token from url and triggers follow-up actions.
   */
  private processSsoTokenFromUrl(): void {
    const ssoToken = String(this.route.snapshot.queryParamMap.get('sso_token') || '').trim();
    if (ssoToken) {
      this.storeSsoToken(ssoToken);
      this.clearSsoCallbackParamsFromUrl();
    }
  }

  /**
   * Clears sso callback params from url to avoid stale values.
   */
  private clearSsoCallbackParamsFromUrl(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams.code;
    delete queryParams.state;
    delete queryParams.error;
    delete queryParams.error_description;
    delete queryParams.sso_token;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  /**
   * Clears side effect query params to avoid stale values.
   */
  private clearSideEffectQueryParams(url: URL): void {
    [
      'token',
      'subscription_action',
      'code',
      'state',
      'error',
      'error_description',
      'sso_token'
    ].forEach((param) => url.searchParams.delete(param));
  }

  /**
   * Returns sso token for request from local storage.
   */
  private getSsoTokenForRequest(): string {
    const win = this.getWindow();
    if (!win) {
      return '';
    }
    try {
      return String(win.localStorage.getItem(STATUS_MAIN_SSO_TOKEN_STORAGE_KEY) || '').trim();
    } catch {
      return '';
    }
  }

  /**
   * Stores sso token in local storage.
   */
  private storeSsoToken(token: string): void {
    const value = String(token || '').trim();
    if (!value) {
      return;
    }
    const win = this.getWindow();
    if (!win) {
      return;
    }
    try {
      win.localStorage.setItem(STATUS_MAIN_SSO_TOKEN_STORAGE_KEY, value);
    } catch {
    }
  }

  /**
   * Clears stored sso token from local storage.
   */
  private clearStoredSsoToken(): void {
    const win = this.getWindow();
    if (!win) {
      return;
    }
    try {
      win.localStorage.removeItem(STATUS_MAIN_SSO_TOKEN_STORAGE_KEY);
    } catch {
    }
  }

  /**
   * Attempts to process subscription token action from url with graceful fallbacks.
   */
  private tryProcessSubscriptionTokenActionFromUrl(org: string, slug: string): void {
    if (this.hasProcessedSubscriptionTokenAction) {
      return;
    }

    const token = String(this.route.snapshot.queryParamMap.get('token') || '').trim();
    if (!token || !org || !slug) {
      return;
    }

    this.hasProcessedSubscriptionTokenAction = true;
    const action = this.getSubscriptionTokenActionFromUrl();
    if (action === 'unsubscribe') {
      this.tryUnsubscribeFromUrl(org, slug, token);
      return;
    }

    this.tryVerifySubscriptionFromUrl(org, slug, token);
  }

  /**
   * Returns subscription token action from url from the current state.
   */
  private getSubscriptionTokenActionFromUrl(): 'verify' | 'unsubscribe' {
    const action = String(this.route.snapshot.queryParamMap.get('subscription_action') || '').trim().toLowerCase();
    if (action === 'unsubscribe' || action === 'verify') {
      return action;
    }
    return this.inferSubscriptionTokenActionFromPath();
  }

  /**
   * Infers subscription action from the current custom-domain path.
   */
  private inferSubscriptionTokenActionFromPath(): 'verify' | 'unsubscribe' {
    if (isAppHost(this.getCurrentHost())) {
      return 'verify';
    }
    const segments = this.getCurrentPathSegments();
    const lastSegment = String(segments.at(-1) || '').trim().toLowerCase();
    return lastSegment === 'unsubscribe' ? 'unsubscribe' : 'verify';
  }

  /**
   * Attempts to verify subscription from url with graceful fallbacks.
   */
  private tryVerifySubscriptionFromUrl(org: string, slug: string, token: string): void {
    const ssoToken = this.getSsoTokenForRequest();
    this.statusPageService.verifyPublicStatusPageSubscription(org, slug, token, ssoToken).subscribe({
      next: (response: any) => {
        if (response?.success === false) {
          this.applyInvalidPublicTokenActionState(this.resolveInvalidTokenActionMessage('verify'));
          return;
        }
        this.openSubscriptionCompleteDialog();
        this.clearSubscriptionTokenParamsFromUrl();
      },
      error: (error: HttpErrorResponse) => {
        this.applyInvalidPublicTokenActionState(
          this.resolveInvalidTokenActionMessage('verify', error)
        );
      }
    });
  }

  /**
   * Attempts to unsubscribe from url with graceful fallbacks.
   */
  private tryUnsubscribeFromUrl(org: string, slug: string, token: string): void {
    const ssoToken = this.getSsoTokenForRequest();
    this.statusPageService.unsubscribePublicStatusPage(org, slug, token, ssoToken).subscribe({
      next: (response: any) => {
        if (response?.success === false) {
          this.applyInvalidPublicTokenActionState(this.resolveInvalidTokenActionMessage('unsubscribe'));
          return;
        }
        this.openUnsubscribeCompleteDialog();
        this.clearSubscriptionTokenParamsFromUrl();
      },
      error: (error: HttpErrorResponse) => {
        this.applyInvalidPublicTokenActionState(
          this.resolveInvalidTokenActionMessage('unsubscribe', error)
        );
      }
    });
  }

  /**
   * Applies invalid public token action state and blocks later page overrides.
   */
  private applyInvalidPublicTokenActionState(message: string): void {
    this.isPublicTokenActionInvalid = true;
    this.applyPublicErrorState(message);
  }

  /**
   * Resolves invalid token-action message with fallback handling.
   */
  private resolveInvalidTokenActionMessage(
    action: 'verify' | 'unsubscribe',
    error?: HttpErrorResponse
  ): string {
    const fallback = action === 'unsubscribe'
      ? 'This unsubscribe link is invalid or expired. Please use the original email link.'
      : 'This verification link is invalid or expired. Please use the original email link.';
    if (!error) {
      return fallback;
    }
    if (error.status === 400 || error.status === 404) {
      return fallback;
    }
    const resolved = this.resolvePublicErrorMessage(error, fallback);
    return resolved || fallback;
  }

  /**
   * Opens subscription complete dialog for the user.
   */
  private openSubscriptionCompleteDialog(): void {
    const dialogThemeConfig = this.getSubscribeDialogThemeConfig();
    this.dialog.open(StatusMainPagePublicSubscriptionDialogComponent, {
      width: '520px',
      maxWidth: '92vw',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: dialogThemeConfig.panelClass,
      data: {
        mode: 'completed',
        isDarkTheme: dialogThemeConfig.isDarkTheme,
        primaryColor: this.primaryColor
      }
    });
  }

  /**
   * Opens unsubscribe complete dialog for the user.
   */
  private openUnsubscribeCompleteDialog(): void {
    const dialogThemeConfig = this.getSubscribeDialogThemeConfig();
    this.dialog.open(StatusMainPagePublicSubscriptionDialogComponent, {
      width: '380px',
      maxWidth: '90vw',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: dialogThemeConfig.panelClass,
      data: {
        mode: 'unsubscribed',
        isDarkTheme: dialogThemeConfig.isDarkTheme,
        primaryColor: this.primaryColor
      }
    });
  }

  /**
   * Clears subscription token params from url to avoid stale values.
   */
  private clearSubscriptionTokenParamsFromUrl(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams.token;
    delete queryParams.subscription_action;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  /**
   * Marks for view for downstream handling.
   */
  private markForView(): void {
    this._cd.markForCheck();
  }

  /**
   * Initializes theme preference before rendering.
   */
  private initializeThemePreference(): void {
    const win = this.getWindow();
    if (!win) {
      return;
    }
    try {
      const queryTheme = this.resolveThemePreferenceFromQuery();
      if (queryTheme) {
        this.isDarkTheme = queryTheme === 'dark';
        this.persistThemePreference();
        return;
      }

      const storedPreference = win.localStorage.getItem(this.themeStorageKey);
      if (storedPreference === 'dark' || storedPreference === 'light') {
        this.isDarkTheme = storedPreference === 'dark';
        return;
      }
      this.isDarkTheme = typeof win.matchMedia === 'function'
        ? win.matchMedia('(prefers-color-scheme: dark)').matches
        : false;
    } catch {
      this.isDarkTheme = false;
    }
  }

  /**
   * Resolves theme preference from route query parameters.
   */
  private resolveThemePreferenceFromQuery(): 'dark' | 'light' | null {
    const raw = String(this.route.snapshot.queryParamMap.get('theme') || '').trim().toLowerCase();
    if (raw === 'dark' || raw === 'dark-theme') {
      return 'dark';
    }
    if (raw === 'light' || raw === 'light-theme') {
      return 'light';
    }
    return null;
  }

  /**
   * Persists theme preference in browser storage when available.
   */
  private persistThemePreference(): void {
    const win = this.getWindow();
    if (!win) {
      return;
    }
    try {
      win.localStorage.setItem(this.themeStorageKey, this.isDarkTheme ? 'dark' : 'light');
    } catch {
    }
  }

  /**
   * Removes one-time theme handoff from the URL after manual user selection.
   */
  private removeThemeQueryParamFromUrl(): void {
    if (!this.route.snapshot.queryParamMap.has('theme')) {
      return;
    }

    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams.theme;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  /**
   * Returns window from the current state.
   */
  private getWindow(): Window | null {
    if (typeof globalThis === 'undefined') {
      return null;
    }
    return (globalThis as { window?: Window }).window ?? null;
  }

  /**
   * Returns subscribe dialog theme config from a single source of truth.
   */
  private getSubscribeDialogThemeConfig(): { isDarkTheme: boolean; panelClass: string[] } {
    const isDarkTheme = this.isDarkTheme;
    return {
      isDarkTheme,
      panelClass: [
        'subscribe-dialog-panel',
        isDarkTheme ? 'status-subscribe-dialog-dark' : 'status-subscribe-dialog-light'
      ]
    };
  }
}
